/**
 * Kunden-Detail — 360-Grad-Ansicht eines Kunden mit Performance, DB, Forecast und Rechnungen.
 *
 * Features:
 * - KPIs: Umsatz, Marge, Cutoff-Quote, Pakete/Monat
 * - Performance-Detail: Picks/Packs pro Stunde, Trend-Charts
 * - Forecast-Vergleich (SOLL vs. IST), Monats-Statistik
 * - Rechnungsliste, Cases, Segment-Override, Portal-Zugang
 *
 * @page /kunden/:id
 * @roles admin, manager
 * @api /api/kunden/:id/detail, /api/kunden/:id/performance, /api/kunden/:id/umsatz-gesamt
 * @api /api/kunden/:id/cutoff-quote, /api/kunden/:id/forecast-vergleich
 * @api /api/kunden/:id/pakete-pro-monat, /api/kunden/:id/monats-statistik
 * @api /api/kunden/:id/rechnungen, /api/kunden/:id/portal-zugang
 * @api /api/deckungsbeitrag/kunde/:id, /api/cases, /api/standorte
 * @i18n customerDetail, common
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, NavLink, useNavigate } from 'react-router-dom';
import { PageHeader, Badge, Button, KpiCard } from '../components/ui';
import BackLink from '../components/BackLink';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LineChart, Line, BarChart, Bar, ComposedChart, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, ReferenceLine } from 'recharts';
import Ring from '../components/Ring';
import UniversalChatter from '../components/UniversalChatter';

// ========== CHART STYLING (SKILL-design.md) ==========
const CHART_GRID = { strokeDasharray: '3 3', vertical: false, stroke: '#F3F4F6' };
const CHART_AXIS = { tick: { fill: '#9CA3AF', fontSize: 12 }, axisLine: false, tickLine: false };
const CHART_ACTIVE_DOT = { r: 6, fill: '#338edc', stroke: '#fff', strokeWidth: 2 };
const CHART_TT_STYLE = {
  contentStyle: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', padding: '12px 16px' },
  labelStyle: { color: '#6B7280', fontSize: 12, marginBottom: 6 },
  itemStyle: { color: '#1A1A2E', fontSize: 14, fontWeight: 500, padding: '2px 0' },
  cursor: { stroke: '#E5E7EB' },
};
const fmtAxisK = v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v;

// ─── Helpers ───────────────────────────────────────────────────────

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  return fetch(path, {
    ...opts,
    headers: { ...headers, ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.message || `HTTP ${r.status}`);
    return data;
  });
}

const segColors = { A: '#338edc', B: '#4D9CE0', C: '#F59E0B' };
const segBg = { A: 'bg-primary-500/15 text-primary-400', B: 'bg-purple-500/15 text-purple-400', C: 'bg-amber-500/15 text-amber-400' };

function fmtEur(n, loc = 'de-DE') {
  if (n == null) return '–';
  return n.toLocaleString(loc, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function fmtEurK(n, loc = 'de-DE') {
  if (n == null) return '–';
  if (Math.abs(n) >= 1000) return (n / 1000).toLocaleString(loc, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'k €';
  return fmtEur(n, loc);
}

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function statusColor(s) {
  if (s === 'Bezahlt') return 'text-green-400 bg-green-500/15';
  if (s === 'Überfällig') return 'text-red-400 bg-red-500/15';
  if (s === 'Storniert') return 'text-[var(--text-tertiary)] bg-surface-inset';
  return 'text-yellow-400 bg-yellow-500/15';
}


// ─── Shared Components ────────────────────────────────────────────

function CardSkeleton({ className = '' }) {
  return (
    <div className={`bg-surface-card border border-line rounded-card p-6 animate-pulse ${className}`}>
      <div className="h-3 w-24 bg-surface-inset rounded mb-3" />
      <div className="h-6 w-32 bg-surface-inset rounded mb-2" />
      <div className="h-3 w-full bg-surface-inset rounded mb-1" />
      <div className="h-3 w-3/4 bg-surface-inset rounded" />
    </div>
  );
}

function Modal({ open, onClose, title, wide, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-surface-elevated border border-line-hover rounded-card w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} max-h-[85vh] overflow-y-auto shadow-2xl`}>
        <div className="flex items-center justify-between p-5 border-b border-line">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-content text-xl leading-none">&times;</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function MiniSpark({ data, color = '#338edc', height = 28, width = 100 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - (v / max) * (height - 4)}`).join(' ');
  return (
    <svg width={width} height={height} className="flex-shrink-0">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CardHeader({ icon, title, children }) {
  return (
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-2">
        <span className="text-[#6B7280]">{icon}</span>
        <h3 className="text-base font-semibold text-[var(--text-primary)]">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// Recharts custom tooltip — polished per design system
function ChartTooltip({ active, payload, label, formatter, locale: tooltipLocale }) {
  if (!active || !payload?.length) return null;
  const loc = tooltipLocale || 'de-DE';
  return (
    <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', padding: '12px 16px' }} className="dark:!bg-[var(--bg-card)] dark:!border-[var(--border-card)]">
      <div style={{ color: '#6B7280', fontSize: 12, marginBottom: 4 }} className="dark:!text-[var(--text-secondary)]">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2" style={{ fontSize: 14 }}>
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
          <span style={{ color: '#6B7280' }} className="dark:!text-[var(--text-secondary)]">{p.name}:</span>
          <span className="font-mono dark:!text-[var(--text-primary)]" style={{ color: '#1A1A2E', fontWeight: 500 }}>{formatter ? formatter(p.value) : p.value?.toLocaleString(loc)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Card: Auftrags- & Zeitprofil ───────────────────────────────

function CardZeitprofil({ kunde, onEdit }) {
  const { t } = useLanguage();
  if (!kunde) return <CardSkeleton />;
  const sopPct = Number(kunde.sop_anteil || 0) * 100;
  const normalPct = 100 - sopPct;
  const pickNormal = Number(kunde.picking_time_per_paket || 0);
  const packNormal = Number(kunde.packing_time_per_paket || 0);
  const pickSop = Number(kunde.avg_sop_pick_zeit || 0);
  const packSop = Number(kunde.avg_sop_pack_zeit || 0);
  const pickGew = (pickNormal * normalPct + pickSop * sopPct) / 100;
  const packGew = (packNormal * normalPct + packSop * sopPct) / 100;

  return (
    <div className="bg-surface-card border border-line rounded-card p-6">
      {/* Section Title */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
          <h3 className="text-base font-semibold text-[var(--text-primary)]">{t('kunden_detail.order_time_profile')}</h3>
        </div>
        {onEdit && (
          <button onClick={onEdit} className="text-xs text-primary-500 hover:text-primary-400 font-medium flex items-center gap-1">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            {t('common.edit')}
          </button>
        )}
      </div>

      {/* Profil-Stats Box */}
      <div className="grid grid-cols-[1fr_1fr_auto] items-center bg-surface-inset rounded-xl px-6 py-5 mb-5">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-1">{t('kunden_detail.avg_skus_order')}</div>
          <div className="text-[26px] font-bold text-content">{Number(kunde.avg_skus_per_order || 1).toFixed(2)}</div>
        </div>
        <div className="pl-6 border-l border-line">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-1">{t('kunden_detail.avg_articles_order')}</div>
          <div className="text-[26px] font-bold text-content">{Number(kunde.avg_articles_per_order || 1).toFixed(2)}</div>
        </div>
        <div className="text-center pl-6">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-1">{t('kunden_detail.sop_share')}</div>
          <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full" style={{ backgroundColor: 'rgba(245,158,11,0.07)', color: '#F59E0B' }}>
            <span className="text-lg font-bold">{sopPct.toFixed(0)}%</span>
            <span className="text-xs font-medium">SOP</span>
          </div>
        </div>
      </div>

      {/* Zeitvorgaben */}
      <div className="mb-5">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-2.5">{t('kunden_detail.time_targets_label')}</div>
        <table className="w-full">
          <tbody>
            <tr className="border-b border-line-subtle">
              <td className="py-2.5 text-xs text-content-tertiary w-[90px]">{t('kunden_detail.normal')}</td>
              <td className="py-2.5 text-[13px] text-content-secondary">Pick <span className="text-content font-semibold">{pickNormal.toFixed(1)}</span> min</td>
              <td className="py-2.5 text-[13px] text-content-secondary text-right">Pack <span className="text-content font-semibold">{packNormal.toFixed(1)}</span> min</td>
            </tr>
            {sopPct > 0 && (
              <tr className="border-b border-line-subtle">
                <td className="py-2.5 text-xs text-content-tertiary">SOP</td>
                <td className="py-2.5 text-[13px] text-content-secondary">Pick <span className="text-content font-semibold">{pickSop.toFixed(2)}</span> min</td>
                <td className="py-2.5 text-[13px] text-content-secondary text-right">Pack <span className="text-content font-semibold">{packSop.toFixed(2)}</span> min</td>
              </tr>
            )}
            <tr>
              <td className="py-2.5 text-xs font-semibold text-content">{t('kunden_detail.weighted')}</td>
              <td className="py-2.5 text-[13px] font-semibold text-content">Pick <span className="text-content">{pickGew.toFixed(1)}</span> min</td>
              <td className="py-2.5 text-[13px] font-semibold text-content text-right">Pack <span className="text-content">{packGew.toFixed(1)}</span> min</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Betriebsparameter */}
      <div className="grid grid-cols-3 gap-4 bg-surface-inset rounded-xl px-5 py-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-0.5">{t('kunden_detail.cutoff_label')}</div>
          <div className="text-base font-semibold text-content">{(() => { const h = Number(kunde.cutoff_stunde || 0); const hh = Math.floor(h); const mm = Math.round((h - hh) * 60); return `${hh}:${String(mm).padStart(2, '0')}`; })()}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-0.5">{t('kunden_detail.before_cutoff_label')}</div>
          <div className="text-base font-semibold text-content">{Math.round(Number(kunde.vor_cutoff_pct || 0))}%</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-0.5">{t('kunden_detail.odoo_id_label')}</div>
          <div className="text-base font-semibold text-content">{kunde.odoo_partner_id || '–'}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-0.5">{t('kunden_detail.oms_name_label')}</div>
          <input
            className="w-full text-sm font-medium bg-transparent border-b border-[var(--border-card)] text-[var(--text-primary)] focus:border-primary-500 focus:outline-none py-0.5 placeholder:text-[var(--text-tertiary)]"
            defaultValue={kunde.oms_name || ''}
            placeholder={t('kunden_detail.oms_name_placeholder')}
            onBlur={e => {
              const val = e.target.value.trim();
              if (val !== (kunde.oms_name || '')) {
                fetch(`${API}/api/kunden/${kunde.id}`, {
                  method: 'PUT', credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ oms_name: val || null }),
                });
                kunde.oms_name = val || null;
              }
            }}
          />
        </div>
      </div>
      {Number(kunde.sop_bulk_pauschale) > 0 && (
        <div className="mt-3 text-[10px] text-[var(--text-tertiary)]">{t('kunden_detail.sop_bulk_flat_rate')}: <span className="font-mono text-[var(--text-secondary)]">{Number(kunde.sop_bulk_pauschale).toFixed(2)}min</span></div>
      )}
    </div>
  );
}

// ─── Card: Cut-Off Quote ────────────────────────────────────────

function CardCutoffQuote({ kundeId, tage }) {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api(`/api/kunden/${kundeId}/cutoff-quote?tage=${tage}`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [kundeId, tage]);

  if (loading) return <CardSkeleton />;
  if (!data || !data.tage || data.tage.length === 0) {
    return (
      <div className="bg-surface-card border border-line rounded-card p-6">
        <CardHeader icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>} title={t('kunden_detail.cutoff_quote')} />
        <div className="text-sm text-[var(--text-tertiary)] py-4 text-center">{t('common.no_data')}</div>
      </div>
    );
  }

  const sparkData = [...data.tage].reverse().map(d => d.pct);
  const targetMet = data.avg_pct >= data.cutoff_target;

  return (
    <div className="bg-surface-card border border-line rounded-card p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
          <h3 className="text-base font-semibold text-[var(--text-primary)]">{t('kunden_detail.cutoff_quote')}</h3>
        </div>
        <span className="text-xs text-content-tertiary">{t('kunden_detail.cutoff_time_display', { hour: data.cutoff_stunde })}</span>
      </div>

      {/* Ring + Stats */}
      <div className="flex items-start gap-7">
        <Ring pct={Math.min(data.avg_pct, 100)} color="#338edc" size={88} stroke={8}>
          <span className="text-base font-bold text-[var(--text-primary)]">{data.avg_pct}%</span>
        </Ring>
        <div className="flex-1">
          <div className="flex justify-between py-2 border-b border-line-subtle">
            <span className="text-[13px] text-[var(--text-secondary)]">{t('kunden_detail.avg_before_cutoff')}</span>
            <span className="text-[13px] font-semibold text-[var(--text-primary)]">{data.avg_pct}%</span>
          </div>
          <div className="flex justify-between py-2 border-b border-line-subtle">
            <span className="text-[13px] text-[var(--text-secondary)]">{t('kunden_detail.target_goal')}</span>
            <span className="text-[13px] font-semibold text-content">{data.cutoff_target}%</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-[13px] text-[var(--text-secondary)]">{t('kunden_detail.analyzed')}</span>
            <span className="text-[13px] font-semibold text-content">{t('kunden_detail.days_label', { count: data.tage.length })}</span>
          </div>
        </div>
      </div>

      {/* Sparkline */}
      <div className="mt-4">
        <MiniSpark data={sparkData} color="#338edc" height={50} width={400} />
      </div>
    </div>
  );
}

// ─── Card: Billing Tracker (Live Monats-Kosten) ────────────────

const CATEGORY_I18N = { Handling: 'billingTracker.categoryHandling', Versand: 'billingTracker.categoryVersand', Verpackung: 'billingTracker.categoryVerpackung' };

function CardBillingTracker({ kundeId }) {
  const { t, locale } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api(`/api/kunden/${kundeId}/billing-tracker`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [kundeId]);

  if (loading) return <CardSkeleton />;
  if (!data || !data.ok || data.current_month?.order_count === 0) {
    return (
      <div className="bg-surface-card border border-line rounded-card p-6">
        <CardHeader
          icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>}
          title={`${t('billingTracker.title')} ${data?.period?.month_label || ''}`}
        />
        <div className="text-sm text-[var(--text-tertiary)] py-4 text-center">{t('billingTracker.noData')}</div>
      </div>
    );
  }

  const cm = data.current_month;
  const proj = data.projection;
  const trend = data.trend;
  const period = data.period;
  const fmtE = (n) => n == null ? '–' : n.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';

  // Confidence badge colors
  const confColor = proj.confidence === 'high' ? 'bg-green-500/15 text-green-500' : proj.confidence === 'medium' ? 'bg-amber-500/15 text-amber-500' : 'bg-[var(--bg-inset)] text-[var(--text-secondary)]';
  const confLabel = t(`billingTracker.projection${proj.confidence.charAt(0).toUpperCase() + proj.confidence.slice(1)}`);

  // Chart data: align current + previous by day-of-month
  const chartData = [];
  const lastCumulative = data.daily_breakdown.length > 0 ? data.daily_breakdown[data.daily_breakdown.length - 1].cumulative : 0;
  const dailyAvg = period.days_elapsed > 0 ? cm.total_netto / period.days_elapsed : 0;
  for (let d = 1; d <= period.days_total; d++) {
    const entry = { day: d };
    const curr = data.daily_breakdown.find(r => new Date(r.date).getDate() === d);
    if (curr) entry.current = curr.cumulative;
    const prev = data.previous_month_daily.find(r => new Date(r.date).getDate() === d);
    if (prev) entry.previous = prev.cumulative;
    // Projection line: overlap at last actual day for seamless connection
    if (d === period.days_elapsed && data.daily_breakdown.length > 0) {
      entry.projected = lastCumulative;
    }
    if (d > period.days_elapsed) {
      entry.projected = lastCumulative + dailyAvg * (d - period.days_elapsed);
    }
    chartData.push(entry);
  }

  // Trend badge
  const trendColor = trend.vs_previous_pct == null ? '' : trend.vs_previous_pct >= 0 ? 'bg-green-500/15 text-green-500' : 'bg-red-500/15 text-red-500';
  const trendSign = trend.vs_previous_pct >= 0 ? '+' : '';

  return (
    <div className="bg-surface-card border border-line rounded-card p-6">
      <CardHeader
        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>}
        title={`${t('billingTracker.title')} ${period.month_label}`}
      >
        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--text-tertiary)]">{t('billingTracker.projection')}</span>
          <span className="text-sm font-bold text-content">~{fmtE(proj.projected_netto)}</span>
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${confColor}`}>{confLabel}</span>
        </div>
      </CardHeader>

      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-[14px] mb-5">
        <div className="bg-surface-inset rounded-xl px-5 py-4 border border-line">
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-1.5">{t('billingTracker.totalNetto')}</div>
          <div className="text-[22px] font-bold text-content">{fmtE(cm.total_netto)}</div>
          <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5">{t('billingTracker.dayProgress', { current: period.days_elapsed, total: period.days_total })}</div>
        </div>
        <div className="bg-surface-inset rounded-xl px-5 py-4 border border-line">
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-1.5">{t('billingTracker.orderCount')}</div>
          <div className="text-[22px] font-bold text-content">{cm.order_count.toLocaleString(locale)}</div>
        </div>
        <div className="bg-surface-inset rounded-xl px-5 py-4 border border-line">
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-1.5">{t('billingTracker.trend')}</div>
          <div className="flex items-center gap-2">
            <div className="text-[22px] font-bold text-content">{trend.vs_previous_pct != null ? `${trendSign}${trend.vs_previous_pct}%` : '–'}</div>
            {trend.vs_previous_pct != null && (
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${trendColor}`}>
                {t('billingTracker.trendNote', { days: period.days_elapsed })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Category Breakdown + By State */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
        {/* Category bars */}
        <div>
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-3">{t('billingTracker.breakdown')}</div>
          <div className="space-y-2.5">
            {cm.by_category.map(cat => (
              <div key={cat.category} className="flex items-center gap-3">
                <div className="w-[80px] text-xs text-[var(--text-secondary)] truncate">{t(CATEGORY_I18N[cat.category] || cat.category)}</div>
                <div className="flex-1 h-[6px] bg-surface-inset rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-[#338edc] transition-all" style={{ width: `${Math.min(cat.pct, 100)}%` }} />
                </div>
                <div className="text-xs font-mono font-semibold text-content w-[70px] text-right">{fmtE(cat.amount)}</div>
                <div className="text-[10px] text-[var(--text-tertiary)] w-[36px] text-right">{cat.pct}%</div>
              </div>
            ))}
          </div>
        </div>

        {/* By State */}
        <div>
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-3">{t('billingTracker.byState')}</div>
          <div className="space-y-2">
            {Object.entries(cm.by_state || {}).map(([state, info]) => {
              const stateLabel = state === 'open' ? t('billingTracker.stateOpen') : state === 'invoiced' ? t('billingTracker.stateInvoiced') : t('billingTracker.statePartial');
              return (
                <div key={state} className="flex items-center justify-between py-1.5 border-b border-line-subtle last:border-0">
                  <span className="text-xs text-[var(--text-secondary)]">{stateLabel}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[var(--text-tertiary)]">{info.count} {t('billingTracker.orderCount').split(' ')[0]}</span>
                    <span className="text-xs font-mono font-semibold text-content">{fmtE(info.amount)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Area Chart: Current vs Previous month (cumulative) */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">{t('billingTracker.dailyChart')}</div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 bg-[#338edc] rounded" />
              <span className="text-[10px] text-[var(--text-secondary)]">{t('billingTracker.currentMonth')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 bg-[#E5E7EB] rounded" />
              <span className="text-[10px] text-[var(--text-secondary)]">{t('billingTracker.previousMonth')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 bg-[#338edc] rounded opacity-40" style={{ borderTop: '1px dashed #338edc' }} />
              <span className="text-[10px] text-[var(--text-secondary)]">{t('billingTracker.projection')}</span>
            </div>
          </div>
        </div>
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <defs>
                <linearGradient id="gradBilling" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#338edc" stopOpacity={0.1} />
                  <stop offset="95%" stopColor="#338edc" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
              <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={v => v % 5 === 0 || v === 1 ? v : ''} />
              <YAxis tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} width={45} />
              <Tooltip content={<ChartTooltip formatter={fmtE} locale={locale} />} />
              <Area type="monotone" dataKey="previous" name={t('billingTracker.previousMonth')} stroke="#9CA3AF" strokeWidth={1.5} strokeDasharray="4 3" fill="none" dot={false} connectNulls />
              <Area type="monotone" dataKey="current" name={t('billingTracker.currentMonth')} stroke="#338edc" strokeWidth={2} fill="url(#gradBilling)" dot={false} activeDot={{ r: 6, fill: '#338edc', stroke: '#fff', strokeWidth: 2 }} connectNulls />
              <Area type="monotone" dataKey="projected" name={t('billingTracker.projection')} stroke="#6366F1" strokeWidth={1.5} strokeDasharray="6 3" fill="none" dot={false} connectNulls />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ─── Card: Umsatz Gesamt ────────────────────────────────────────

function CardUmsatzGesamt({ kundeId }) {
  const { t, locale, tMonthShort } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api(`/api/kunden/${kundeId}/umsatz-gesamt`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [kundeId]);

  if (loading) return <CardSkeleton className="col-span-full" />;
  if (!data || data.error || !data.gesamt || data.gesamt.count === 0) {
    return (
      <div className="bg-surface-card border border-line rounded-card p-6 col-span-full">
        <CardHeader icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>} title={t('kunden_detail.revenue_total')} />
        <div className="text-sm text-[var(--text-tertiary)] py-4 text-center">{data?.error || t('kunden_detail.no_invoice_data')}</div>
      </div>
    );
  }

  const g = data.gesamt;
  const fmtE = (n) => fmtEur(n, locale);
  const fmtEK = (n) => fmtEurK(n, locale);
  const chartData = data.monatlich.map(m => {
    const [y, mo] = m.monat.split('-');
    return { name: tMonthShort(parseInt(mo) - 1) + ' ' + y.slice(2), outbound: m.outbound, lager: m.lager, sonstiges: m.sonstiges, total: m.total };
  });

  return (
    <div className="bg-surface-card border border-line rounded-card p-6 col-span-full">
      <CardHeader
        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>}
        title={t('kunden_detail.revenue_total')}
      >
        <span className="text-xs text-content-tertiary">{t('kunden_detail.invoices_months', { count: g.count })}</span>
      </CardHeader>

      {/* Revenue summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-[14px] mb-5">
        <div className="bg-surface-inset rounded-xl px-5 py-4 border border-line">
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-1.5">{t('kunden_detail.total_label')}</div>
          <div className="text-[18px] font-bold text-content">{fmtE(g.total)}</div>
        </div>
        <div className="bg-surface-inset rounded-xl px-5 py-4 border border-line">
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-1.5">{t('kunden_detail.outbound_shipping')}</div>
          <div className="text-[18px] font-bold text-content">{fmtE(g.outbound)}</div>
        </div>
        <div className="bg-surface-inset rounded-xl px-5 py-4 border border-line">
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-1.5">{t('kunden_detail.storage_fees')}</div>
          <div className="text-[18px] font-bold text-content">{fmtE(g.lager)}</div>
        </div>
        <div className="bg-surface-inset rounded-xl px-5 py-4 border border-line">
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-1.5">{t('kunden_detail.other')}</div>
          <div className="text-[18px] font-bold text-content">{fmtE(g.sonstiges)}</div>
        </div>
      </div>

      {/* Line chart */}
      {chartData.length >= 2 && (
        <div className="h-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <defs>
                <linearGradient id="gradUmsatz" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#338edc" stopOpacity={0.1} />
                  <stop offset="95%" stopColor="#338edc" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={v => fmtEK(v)} width={55} />
              <Tooltip content={<ChartTooltip formatter={fmtE} locale={locale} />} />
              <Area type="monotone" dataKey="total" name={t('kunden_detail.total_legend')} stroke="#338edc" strokeWidth={2} fill="url(#gradUmsatz)" dot={false} activeDot={{ r: 6, fill: '#338edc', stroke: '#fff', strokeWidth: 2 }} />
              <Area type="monotone" dataKey="outbound" name={t('kunden_detail.outbound_legend')} stroke="#22D3EE" strokeWidth={1.5} fill="none" dot={false} strokeDasharray="4 2" />
              <Area type="monotone" dataKey="lager" name={t('kunden_detail.storage_legend')} stroke="#6366F1" strokeWidth={1.5} fill="none" dot={false} strokeDasharray="4 2" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="flex items-center gap-4 mt-2">
        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#338edc]" /><span className="text-xs text-content-secondary">{t('kunden_detail.total_legend')}</span></div>
        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#22D3EE]" /><span className="text-xs text-content-secondary">{t('kunden_detail.outbound_legend')}</span></div>
        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#6366F1]" /><span className="text-xs text-content-secondary">{t('kunden_detail.storage_legend')}</span></div>
      </div>
    </div>
  );
}

// ─── Card: Lagervolumen ────────────────────────────────────────

function CardLagervolumen({ kundeId }) {
  const { t, locale, tMonthShort } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api(`/api/kunden/${kundeId}/lagervolumen`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [kundeId]);

  if (loading) return <CardSkeleton />;
  if (!data || data.error) return null;

  const { aktuell, historie } = data;
  const hasData = aktuell.m3 > 0 || aktuell.m3_vormonat > 0 || historie.length > 0;
  if (!hasData) return null;

  // avg_daily_m3: aktuell.m3 now stores avg daily m³ from sync
  const displayM3 = aktuell.m3 > 0 ? aktuell.m3 : aktuell.m3_vormonat;
  const displayKosten = aktuell.m3 > 0 ? aktuell.kosten : aktuell.kosten_vormonat;
  const isVormonat = aktuell.m3 <= 0 && aktuell.m3_vormonat > 0;

  // Current month entry for tage_mit_daten / kosten_pro_tag
  const curEntry = historie.length > 0 ? historie[historie.length - 1] : null;
  const tageMitDaten = curEntry?.tage_mit_daten || 0;
  const kostenProTag = curEntry?.kosten_pro_tag || (tageMitDaten > 0 ? displayKosten / tageMitDaten : 0);

  // Helper: parse "YYYY-MM-DD" string without timezone issues
  const parseMonat = (m) => {
    if (!m) return { y: 0, mo: 0, str: '' };
    const s = String(m).slice(0, 10); // "YYYY-MM-DD"
    const [y, mo] = s.split('-').map(Number);
    return { y, mo, str: `${y}-${String(mo).padStart(2, '0')}` };
  };

  // Delta: compare projected current month costs vs. full previous month costs
  let deltaKosten = null;
  let isProjected = false;
  if (historie.length >= 2 && !isVormonat) {
    const cur = historie[historie.length - 1];
    const prev = historie[historie.length - 2];
    const curTage = cur.tage_mit_daten || 1;
    const now = new Date();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const curMonatStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const entryMonatStr = parseMonat(cur.monat).str;
    // If current entry is the running month, project costs
    if (entryMonatStr === curMonatStr && curTage < daysInMonth) {
      const projected = (cur.kosten / curTage) * daysInMonth;
      if (prev.kosten > 0) deltaKosten = ((projected - prev.kosten) / prev.kosten * 100);
      isProjected = true;
    } else {
      if (prev.kosten > 0) deltaKosten = ((cur.kosten - prev.kosten) / prev.kosten * 100);
    }
  } else if (aktuell.kosten_vormonat > 0 && aktuell.kosten > 0) {
    deltaKosten = ((aktuell.kosten - aktuell.kosten_vormonat) / aktuell.kosten_vormonat * 100);
  }

  // Chart data: bars = costs (EUR), line = avg daily m³
  const chartData = historie.map(h => {
    const { y, mo } = parseMonat(h.monat);
    return {
      name: tMonthShort(mo - 1) + ' ' + String(y).slice(2),
      kosten: h.kosten,
      avgM3: h.avg_daily_m3 || (h.tage_mit_daten > 0 ? Math.round(h.m3 / h.tage_mit_daten * 10) / 10 : 0),
      tage: h.tage_mit_daten || 0,
    };
  });

  const fmtN = (n) => n.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const fmtE = (n) => n.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  return (
    <div className="bg-surface-card border border-line rounded-card p-6">
      <CardHeader
        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0022 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>}
        title={t('kunden_detail.lager_title')}
      >
        {aktuell.synced_at && (
          <span className="text-[10px] text-content-tertiary">
            {t('kunden_detail.lager_synced', { date: new Date(aktuell.synced_at).toLocaleDateString(locale) })}
          </span>
        )}
      </CardHeader>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-[14px] mb-5">
        <div className="bg-surface-inset rounded-xl px-5 py-4 border border-line">
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-1.5">{t('kunden_detail.lager_avg_m3')}</div>
          <div className="text-[18px] font-bold text-content">{fmtN(displayM3)} m³</div>
          <div className="text-[10px] text-content-tertiary mt-0.5">{isVormonat ? t('kunden_detail.lager_prev_month') : t('kunden_detail.lager_per_day')}</div>
        </div>
        <div className="bg-surface-inset rounded-xl px-5 py-4 border border-line">
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-1.5">{t('kunden_detail.lager_cost')}</div>
          <div className="text-[18px] font-bold text-content">{fmtE(displayKosten)} €</div>
          <div className="text-[10px] text-content-tertiary mt-0.5">{isVormonat ? t('kunden_detail.lager_prev_month') : t('kunden_detail.lager_current_month')}</div>
        </div>
        <div className="bg-surface-inset rounded-xl px-5 py-4 border border-line">
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-1.5">{t('kunden_detail.lager_delta')}</div>
          {deltaKosten !== null ? (
            <div className="text-[18px] font-bold text-content">
              <span className={`inline-flex items-center gap-1 text-[12px] font-semibold px-2 py-0.5 rounded-full ${deltaKosten > 1 ? 'bg-emerald-500/15 text-emerald-400' : deltaKosten < -1 ? 'bg-red-500/15 text-red-400' : 'bg-surface-active text-content-secondary'}`}>
                {deltaKosten > 0 ? '+' : ''}{deltaKosten.toFixed(1)}%
              </span>
            </div>
          ) : <div className="text-[18px] font-bold text-content-tertiary">–</div>}
          {isProjected && <div className="text-[10px] text-content-tertiary mt-0.5">{t('kunden_detail.lager_projected')}</div>}
        </div>
        <div className="bg-surface-inset rounded-xl px-5 py-4 border border-line">
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-1.5">{t('kunden_detail.lager_cost_per_day')}</div>
          <div className="text-[18px] font-bold text-content">
            {kostenProTag > 0 ? fmtE(kostenProTag) + ' €' : '–'}
          </div>
          {tageMitDaten > 0 && <div className="text-[10px] text-content-tertiary mt-0.5">{t('kunden_detail.lager_days', { days: tageMitDaten })}</div>}
        </div>
      </div>

      {/* ComposedChart: cost bars (EUR) + avg m³/day line */}
      {chartData.length >= 2 && (
        <div className="h-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v} width={45} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={40} />
              <Tooltip content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const k = payload.find(p => p.dataKey === 'kosten');
                const m = payload.find(p => p.dataKey === 'avgM3');
                const tg = payload[0]?.payload?.tage;
                return (
                  <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', padding: '12px 16px' }} className="dark:!bg-[var(--bg-card)] dark:!border-[var(--border-card)]">
                    <div style={{ color: '#6B7280', fontSize: 12, marginBottom: 4 }} className="font-semibold dark:!text-[var(--text-secondary)]">{label}</div>
                    {k && <div style={{ fontSize: 14, color: '#1A1A2E' }} className="dark:!text-[var(--text-primary)]"><span className="inline-block w-2 h-2 rounded-full bg-[#338edc] mr-1.5" />{fmtE(k.value)} €</div>}
                    {m && m.value > 0 && <div style={{ fontSize: 14, color: '#1A1A2E' }} className="dark:!text-[var(--text-primary)]"><span className="inline-block w-2 h-2 rounded-full bg-[#22D3EE] mr-1.5" />{fmtN(m.value)} m³/{t('kunden_detail.lager_per_day_short')}</div>}
                    {tg > 0 && <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }} className="dark:!text-[var(--text-tertiary)]">{t('kunden_detail.lager_days', { days: tg })}</div>}
                  </div>
                );
              }} />
              <Bar yAxisId="left" dataKey="kosten" name={t('kunden_detail.lager_cost')} fill="#338edc" radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="avgM3" name={t('kunden_detail.lager_avg_m3')} stroke="#22D3EE" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#338edc]" /><span className="text-xs text-content-secondary">{t('kunden_detail.lager_cost')}</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2 h-0.5 bg-[#22D3EE]" style={{ borderTop: '1.5px dashed #22D3EE' }} /><span className="text-xs text-content-secondary">{t('kunden_detail.lager_avg_m3_legend')}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Card: Deckungsbeitrag ──────────────────────────────────────

function CardDeckungsbeitrag({ kundeId, tage }) {
  const { t, locale } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const now = new Date();
    const bis = isoDate(now);
    const von = isoDate(new Date(now.getTime() - tage * 86400000));
    setLoading(true);
    api(`/api/deckungsbeitrag/kunde/${kundeId}?von=${von}&bis=${bis}`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [kundeId, tage]);

  const fmtE = (n) => fmtEur(n, locale);

  if (loading) return <CardSkeleton className="col-span-full" />;
  if (!data || !data.summary || data.summary.auftraege === 0) {
    return (
      <div className="bg-surface-card border border-line rounded-card p-6 col-span-full">
        <div className="flex items-center gap-2 mb-2"><div className="w-[3px] h-[18px] rounded-[2px] bg-[#338edc] shrink-0" /><div className="text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('kunden_detail.contribution_margin')}</div></div>
        <div className="text-sm text-[var(--text-tertiary)] py-4 text-center">{t('kunden_detail.no_cm_data')}</div>
      </div>
    );
  }

  const s = data.summary;
  return (
    <div className="bg-surface-card border border-line rounded-card p-6 col-span-full">
      <CardHeader
        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>}
        title={t('kunden_detail.contribution_margin')}
      >
        <NavLink to={`/deckungsbeitrag?kunde=${kundeId}`} className="text-[10px] text-forecast hover:underline">{t('kunden_detail.details_link')} &rarr;</NavLink>
      </CardHeader>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div className="bg-surface rounded-card p-3 border border-line">
          <div className="text-[9px] text-[var(--text-secondary)] uppercase">{t('kunden_detail.orders')}</div>
          <div className="font-mono text-lg font-bold text-content">{s.auftraege.toLocaleString(locale)}</div>
        </div>
        <div className="bg-surface rounded-card p-3 border border-line">
          <div className="text-[9px] text-[var(--text-secondary)] uppercase">{t('kunden_detail.outbound_revenue')}</div>
          <div className="text-[7px] text-[var(--text-tertiary)]">{t('kunden_detail.shipping_only')}</div>
          <div className="font-mono text-lg font-bold text-content">{fmtE(s.umsatz)}</div>
        </div>
        <div className="bg-surface rounded-card p-3 border border-line">
          <div className="text-[9px] text-[var(--text-secondary)] uppercase">{t('kunden_detail.costs')}</div>
          <div className="font-mono text-lg font-bold text-content">{fmtE(s.kosten)}</div>
        </div>
        <div className="bg-surface rounded-card p-3 border border-line">
          <div className="text-[9px] text-[var(--text-secondary)] uppercase">{t('kunden_detail.cm_total')}</div>
          <div className="font-mono text-lg font-bold text-content">{fmtE(s.db)}</div>
        </div>
        <div className="bg-surface rounded-card p-3 border border-line">
          <div className="text-[9px] text-[var(--text-secondary)] uppercase">{t('kunden_detail.margin_label')}</div>
          <div className="mt-0.5 inline-flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${s.marge_pct >= 25 ? 'bg-green-500' : s.marge_pct >= 15 ? 'bg-yellow-500' : s.marge_pct >= 10 ? 'bg-orange-500' : 'bg-red-500'}`} /><span className="text-xs font-bold text-[var(--text-primary)]">{s.marge_pct.toFixed(1)}%</span></div>
          <div className="text-[9px] text-[var(--text-tertiary)] mt-0.5">{t('kunden_detail.avg_cm_per_order', { amount: fmtE(s.avg_db_pro_auftrag) })}</div>
        </div>
      </div>

      {/* Strategische KPIs */}
      {(s.komplexitaet_index != null || s.effizienz_index != null || s.db_pro_stunde != null) && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-surface rounded-card p-3 border border-line">
            <div className="text-[9px] text-[var(--text-secondary)] uppercase">{t('cm.komplexitaetIndex')}</div>
            <div className="font-mono text-lg font-bold text-content">{s.komplexitaet_index != null ? `${s.komplexitaet_index.toFixed(1).replace('.', ',')} min` : '–'}</div>
            <div className="text-[9px] text-[var(--text-tertiary)] mt-0.5">{s.komplexitaet_index != null ? (s.komplexitaet_index < 1 ? t('cm.einfach') : s.komplexitaet_index < 3 ? t('cm.mittel') : t('cm.komplex')) : ''}</div>
          </div>
          <div className="bg-surface rounded-card p-3 border border-line">
            <div className="text-[9px] text-[var(--text-secondary)] uppercase">{t('cm.effizienzIndex')}</div>
            <div className="font-mono text-lg font-bold text-content">{s.effizienz_index != null ? `${s.effizienz_index.toFixed(0)}%` : '–'}</div>
            <div className="text-[9px] mt-0.5 text-[var(--text-secondary)]">{t('cm.sollVsIst')}</div>
          </div>
          <div className="bg-surface rounded-card p-3 border border-line">
            <div className="text-[9px] text-[var(--text-secondary)] uppercase">{t('cm.dbProStunde')}</div>
            <div className="font-mono text-lg font-bold text-content">{s.db_pro_stunde != null ? `${s.db_pro_stunde.toFixed(2).replace('.', ',')} €/h` : '–'}</div>
            <div className="text-[9px] mt-0.5 text-[var(--text-secondary)]">
              {(s.db_pro_stunde || 0) >= 20 ? t('cm.profitabel') : (s.db_pro_stunde || 0) >= 10 ? t('cm.grenzwertig') : t('cm.kritisch')}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-surface rounded-card border border-line overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                <th className="px-3 py-2 text-[10px] font-medium text-[var(--text-secondary)] uppercase">{t('kunden_detail.category')}</th>
                <th className="px-3 py-2 text-[10px] font-medium text-[var(--text-secondary)] uppercase text-right">{t('kunden_detail.revenue_label')}</th>
                <th className="px-3 py-2 text-[10px] font-medium text-[var(--text-secondary)] uppercase text-right">{t('kunden_detail.costs')}</th>
                <th className="px-3 py-2 text-[10px] font-medium text-[var(--text-secondary)] uppercase text-right">{t('kunden_detail.cm_short')}</th>
              </tr>
            </thead>
            <tbody>
              {[
                { labelKey: 'kunden_detail.handling', key: 'handling', color: '#338edc' },
                { labelKey: 'kunden_detail.packaging', key: 'verpackung', color: '#4D9CE0' },
                { labelKey: 'kunden_detail.shipping_cat', key: 'versand', color: '#06b6d4' },
              ].map(cat => {
                const b = s.breakdown?.[cat.key] || {};
                const catDb = (b.umsatz || 0) - (b.kosten || 0);
                return (
                  <tr key={cat.key} className="border-b border-line hover:bg-surface-card transition-colors">
                    <td className="px-3 py-2 text-xs"><span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: cat.color }} />{t(cat.labelKey)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-right text-content-secondary">{fmtE(b.umsatz || 0)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-right text-content-secondary">{fmtE(b.kosten || 0)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-right font-bold text-content">{fmtE(catDb)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {data.trend && data.trend.length >= 2 && (
          <div className="bg-surface rounded-card border border-line p-4">
            <div className="flex items-center gap-2 mb-2"><div className="w-[3px] h-[18px] rounded-[2px] bg-[#338edc] shrink-0" /><div className="text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('kunden_detail.monthly_trend_cm')}</div></div>
            <svg viewBox="0 0 300 80" className="w-full" style={{ height: 80 }}>
              {(() => {
                const tr = data.trend;
                const maxVal = Math.max(...tr.map(d => Math.max(Math.abs(d.db), d.umsatz)), 1);
                const w = 300, h = 80, pad = 4;
                const uPts = tr.map((d, i) => `${pad + (i / (tr.length - 1)) * (w - pad * 2)},${h - pad - (d.umsatz / maxVal) * (h - pad * 2)}`).join(' ');
                const dPts = tr.map((d, i) => `${pad + (i / (tr.length - 1)) * (w - pad * 2)},${h - pad - (Math.max(d.db, 0) / maxVal) * (h - pad * 2)}`).join(' ');
                return (
                  <>
                    <polyline points={uPts} fill="none" stroke="#338edc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
                    <polyline points={dPts} fill="none" stroke="#22D3EE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    {tr.map((d, i) => (
                      <circle key={i} cx={pad + (i / (tr.length - 1)) * (w - pad * 2)} cy={h - pad - (Math.max(d.db, 0) / maxVal) * (h - pad * 2)} r="3" fill="#22D3EE" />
                    ))}
                  </>
                );
              })()}
            </svg>
            <div className="flex items-center gap-3 mt-1">
              <div className="flex items-center gap-1"><div className="w-3 h-0.5 bg-primary-400 rounded opacity-50" /><span className="text-[9px] text-[var(--text-secondary)]">{t('kunden_detail.revenue_legend')}</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-0.5 bg-[#22D3EE] rounded" /><span className="text-[9px] text-[var(--text-secondary)]">{t('kunden_detail.cm_legend')}</span></div>
              <span className="text-[9px] text-[var(--text-tertiary)] ml-auto">{data.trend[0].monat} – {data.trend[data.trend.length - 1].monat}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Card: Performance (enhanced) ────────────────────────────────

function CardPerformance({ kundeId, tage }) {
  const { t, locale } = useLanguage();
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api(`/api/kunden/${kundeId}/performance?tage=${tage}`).catch(() => null),
      api(`/api/kunden/${kundeId}/performance-detail?tage=${tage}`).catch(() => null),
    ]).then(([perf, det]) => { setData(perf); setDetail(det); })
      .finally(() => setLoading(false));
  }, [kundeId, tage]);

  if (loading) return <CardSkeleton className="col-span-full" />;
  if (!data || (data.pick.waves === 0 && data.pack.packs === 0)) {
    return (
      <div className="bg-surface-card border border-line rounded-card p-6 col-span-full">
        <CardHeader icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>} title={t('kunden_detail.performance_title')} />
        <div className="text-sm text-[var(--text-tertiary)] py-4 text-center">{t('common.no_data')}</div>
      </div>
    );
  }

  const pickSparkData = data.pick_daily.map(d => d.avg_min);
  const packSparkData = data.pack_daily.map(d => d.avg_min);
  const vorgaben = detail?.vorgaben;
  const sopSplit = detail?.sop_split;

  return (
    <div className="bg-surface-card border border-line rounded-card p-6 col-span-full">
      <CardHeader
        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>}
        title={t('kunden_detail.performance_title')}
      >
        <NavLink to={`/performance?kunde=${kundeId}`} className="text-[10px] text-forecast hover:underline">{t('kunden_detail.details_link')} &rarr;</NavLink>
      </CardHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        {/* Pick section */}
        <div className="bg-surface rounded-card p-3 border border-line">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] text-[var(--text-secondary)] uppercase font-bold">{t('kunden_detail.pick_label')}</span>
            <MiniSpark data={pickSparkData} color="#338edc" height={20} width={60} />
          </div>
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <div><span className="font-mono text-sm font-bold text-content">{data.pick.waves.toLocaleString(locale)}</span><span className="text-[10px] text-[var(--text-secondary)] ml-1">{t('kunden_detail.waves_label')}</span></div>
            <div>
              <span className="font-mono text-sm font-bold text-[var(--text-primary)]">{data.pick.avg_min}</span>
              <span className="text-[10px] text-[var(--text-secondary)] ml-1">{t('kunden_detail.min_per_wave')}</span>
              {vorgaben && vorgaben.pick_normal > 0 && (
                <span className="text-[9px] ml-1" style={{ color: data.pick.avg_min <= vorgaben.pick_normal ? '#34d399' : '#ef4444' }}>
                  ({t('kunden_detail.target_time', { time: vorgaben.pick_normal })})
                </span>
              )}
            </div>
          </div>
          {/* SOP split */}
          {sopSplit && (sopSplit.sop_waves > 0 || sopSplit.normal_waves > 0) && (
            <div className="flex gap-2 text-[10px] mb-2">
              <span className="bg-green-500/10 text-green-400 px-1.5 py-0.5 rounded">{t('kunden_detail.sop_waves_label', { waves: sopSplit.sop_waves, avg: sopSplit.sop_avg_min })}</span>
              <span className="bg-primary-500/10 text-primary-400 px-1.5 py-0.5 rounded">{t('kunden_detail.normal_waves_label', { waves: sopSplit.normal_waves, avg: sopSplit.normal_avg_min })}</span>
            </div>
          )}
          {/* Mixed-wave hint */}
          {detail?.mixed_wave && (
            <div className="flex gap-2 text-[10px] mb-2">
              <span className="bg-amber-500/10 text-amber-400 px-1.5 py-0.5 rounded" title={t('kunden_detail.mixed_wave_info', { pct: detail.mixed_wave.mixed_pct, count: detail.mixed_wave.mixed_auftraege, avg: detail.mixed_wave.avg_pick_anteil })}>
                {t('kunden_detail.mixed_wave_info', { pct: detail.mixed_wave.mixed_pct, count: detail.mixed_wave.mixed_auftraege, avg: detail.mixed_wave.avg_pick_anteil })}
              </span>
            </div>
          )}
          {/* Top pickers table */}
          {detail?.pickers && detail.pickers.length > 0 && (
            <div className="mt-2">
              <div className="text-[9px] text-[var(--text-secondary)] uppercase mb-1">{t('kunden_detail.top_pickers')}</div>
              <div className="space-y-1">
                {detail.pickers.slice(0, 5).map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-[10px]">
                    <span className="text-content-secondary">{i + 1}. {p.name}</span>
                    <span className="font-mono text-[var(--text-secondary)]">{p.articles.toLocaleString(locale)} {t('kunden_detail.articles_short')} | {p.waves} W | Ø {p.avg_min}min</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Pack section */}
        <div className="bg-surface rounded-card p-3 border border-line">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] text-[var(--text-secondary)] uppercase font-bold">{t('kunden_detail.pack_label')}</span>
            <MiniSpark data={packSparkData} color="#4D9CE0" height={20} width={60} />
          </div>
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <div><span className="font-mono text-sm font-bold text-content">{data.pack.packs.toLocaleString(locale)}</span><span className="text-[10px] text-[var(--text-secondary)] ml-1">{t('kunden_detail.packages_count')}</span></div>
            <div>
              <span className="font-mono text-sm font-bold text-[var(--text-primary)]">{data.pack.avg_min}</span>
              <span className="text-[10px] text-[var(--text-secondary)] ml-1">{t('kunden_detail.min_per_package')}</span>
              {vorgaben && vorgaben.pack_normal > 0 && (
                <span className="text-[9px] ml-1" style={{ color: data.pack.avg_min <= vorgaben.pack_normal ? '#34d399' : '#ef4444' }}>
                  ({t('kunden_detail.target_time', { time: vorgaben.pack_normal })})
                </span>
              )}
            </div>
          </div>
          {/* Top packers table */}
          {detail?.packers && detail.packers.length > 0 && (
            <div className="mt-2">
              <div className="text-[9px] text-[var(--text-secondary)] uppercase mb-1">{t('kunden_detail.top_packers')}</div>
              <div className="space-y-1">
                {detail.packers.slice(0, 5).map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-[10px]">
                    <span className="text-content-secondary">{i + 1}. {p.name}</span>
                    <span className="font-mono text-[var(--text-secondary)]">{p.packages.toLocaleString(locale)} {t('kunden_detail.pkt_short')} | {p.packs} {t('kunden_detail.runs')} | Ø {p.avg_min}min</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Card: Forecast vs IST (enhanced) ───────────────────────────

function CardForecastVsIst({ kundeId }) {
  const { t, locale, tMonthShort, tMonth } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api(`/api/kunden/${kundeId}/forecast-vergleich`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [kundeId]);

  if (loading) return <CardSkeleton />;
  if (!data || !data.monate || data.monate.length === 0) {
    return (
      <div className="bg-surface-card border border-line rounded-card p-6">
        <CardHeader icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"/><path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/></svg>} title={t('kunden_detail.forecast_vs_actual')} />
        <div className="text-sm text-[var(--text-tertiary)] py-4 text-center">{t('kunden_detail.no_forecast_available')}</div>
      </div>
    );
  }

  const chartData = data.monate.map(m => ({
    name: tMonthShort(m.monat - 1) + ' ' + String(m.jahr).slice(2),
    soll: m.soll,
    ist: m.ist,
    erfuellung: m.erfuellung,
  }));

  // Overall stats from current or latest month with IST data
  const latest = [...data.monate].reverse().find(m => m.ist > 0) || data.monate[data.monate.length - 1];

  return (
    <div className="bg-surface-card border border-line rounded-card p-6">
      <CardHeader
        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"/><path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/></svg>}
        title={t('kunden_detail.forecast_vs_actual')}
      >
        <NavLink to={`/forecast?kunde=${kundeId}`} className="text-[10px] text-forecast hover:underline">Forecast &rarr;</NavLink>
      </CardHeader>

      {latest && (
        <div className="flex items-center gap-3 mb-3">
          <Ring pct={Math.min(latest.erfuellung, 100)} color="#338edc" size={48} stroke={4}>
            <span className="text-[10px] font-mono font-bold text-content">{latest.erfuellung.toFixed(0)}%</span>
          </Ring>
          <div>
            <div className="text-[10px] text-[var(--text-secondary)]">{t('kunden_detail.soll')}: <span className="font-mono text-content font-bold">{latest.soll.toLocaleString(locale)}</span></div>
            <div className="text-[10px] text-[var(--text-secondary)]">{t('kunden_detail.ist')}: <span className="font-mono text-content font-bold">{latest.ist > 0 ? latest.ist.toLocaleString(locale) : '–'}</span></div>
            <div className="text-[9px] text-[var(--text-tertiary)]">{tMonth(latest.monat - 1)} {latest.jahr}</div>
          </div>
        </div>
      )}

      {/* Bar chart for 6 months */}
      {chartData.length >= 2 && (
        <div className="h-[120px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={35} tickFormatter={v => v >= 1000 ? (v/1000).toFixed(0) + 'k' : v} />
              <Tooltip content={<ChartTooltip locale={locale} />} />
              <Bar dataKey="soll" name={t('kunden_detail.soll')} fill="#E5E7EB" radius={[4, 4, 0, 0]} />
              <Bar dataKey="ist" name={t('kunden_detail.ist')} fill="#338edc" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="flex items-center gap-3 mt-1">
        <div className="flex items-center gap-1"><div className="w-3 h-2 bg-[#E5E7EB] rounded-sm" /><span className="text-[9px] text-[var(--text-secondary)]">{t('kunden_detail.soll')}</span></div>
        <div className="flex items-center gap-1"><div className="w-3 h-2 bg-[#338edc] rounded-sm" /><span className="text-[9px] text-[var(--text-secondary)]">{t('kunden_detail.ist')}</span></div>
      </div>
    </div>
  );
}

// ─── Card: Versendete Pakete (replaces Fulfillment) ──────────────

function CardVersendetePakete({ kundeId }) {
  const { t, locale, tMonthShort } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api(`/api/kunden/${kundeId}/pakete-pro-monat`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [kundeId]);

  if (loading) return <CardSkeleton />;
  if (!data || !data.monate || data.monate.length === 0) {
    return (
      <div className="bg-surface-card border border-line rounded-card p-6">
        <CardHeader icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2"><rect x="1" y="3" width="15" height="13"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>} title={t('kunden_detail.shipped_packages')} />
        <div className="text-sm text-[var(--text-tertiary)] py-4 text-center">{t('common.no_data')}</div>
      </div>
    );
  }

  const totalPakete = data.monate.reduce((s, m) => s + m.pakete, 0);
  const chartData = data.monate.map(m => {
    const [y, mo] = m.monat.split('-');
    return { name: tMonthShort(parseInt(mo) - 1) + ' ' + y.slice(2), pakete: m.pakete };
  });

  return (
    <div className="bg-surface-card border border-line rounded-card p-6">
      <CardHeader
        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2"><rect x="1" y="3" width="15" height="13"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>}
        title={t('kunden_detail.shipped_packages')}
      >
        <span className="text-[9px] text-[var(--text-tertiary)]">{t('kunden_detail.months_total', { total: totalPakete.toLocaleString(locale) })}</span>
      </CardHeader>

      {chartData.length >= 2 && (
        <div className="h-[140px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={40} tickFormatter={v => v >= 1000 ? (v/1000).toFixed(0) + 'k' : v} />
              <Tooltip content={<ChartTooltip locale={locale} />} />
              <Bar dataKey="pakete" name={t('kunden_detail.packages_count')} fill="#338edc" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ─── Card: Paketgewicht (KPI + 12-Monats-Trend) ─────────────────

function CardPaketgewicht({ kundeId, tage }) {
  const { t, locale, tMonthShort } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api(`/api/kunden/${kundeId}/gewicht-trend?tage=${tage}`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [kundeId, tage]);

  if (loading) return <CardSkeleton />;
  if (!data || !data.current || data.current.pakete === 0) return null;

  const { current, trend } = data;
  const chartData = trend.map(m => {
    const [y, mo] = m.monat.split('-');
    return { name: tMonthShort(parseInt(mo) - 1) + ' ' + y.slice(2), avg_kg: m.avg_kg, pakete: m.pakete, total_kg: m.total_kg };
  });

  return (
    <div className="bg-surface-card border border-line rounded-card p-6">
      <CardHeader
        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.9 18.55l-8-15.98a1 1 0 00-1.8 0l-8 15.98"/><line x1="2" y1="19" x2="22" y2="19"/></svg>}
        title={t('kunden_detail.weight_trend')}
      >
        <span className="text-[9px] text-[var(--text-tertiary)]">{current.pakete.toLocaleString(locale)} {t('kunden_detail.packages_count')}</span>
      </CardHeader>

      {/* KPI row */}
      <div className="flex items-center gap-4 mb-3">
        <div>
          <div className="text-[22px] font-bold text-content">{current.avg_kg.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg</div>
          <div className="text-[10px] text-[var(--text-secondary)]">{t('kunden_detail.avg_weight')}</div>
        </div>
        {current.delta_pct !== null && (
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${current.delta_pct > 1 ? 'bg-emerald-500/15 text-emerald-400' : current.delta_pct < -1 ? 'bg-red-500/15 text-red-400' : 'bg-surface-active text-content-secondary'}`}>
            {current.delta_pct > 0 ? '+' : ''}{current.delta_pct.toFixed(1)}%
          </span>
        )}
      </div>

      {/* Line chart: avg kg per month */}
      {chartData.length >= 2 && (
        <div className="h-[140px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <defs>
                <linearGradient id="gradWeight" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#338edc" stopOpacity={0.1} />
                  <stop offset="95%" stopColor="#338edc" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={35} domain={['auto', 'auto']} tickFormatter={v => v.toFixed(1)} />
              <Tooltip content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0]?.payload;
                return (
                  <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', padding: '12px 16px' }} className="dark:!bg-[var(--bg-card)] dark:!border-[var(--border-card)]">
                    <div style={{ color: '#6B7280', fontSize: 12, marginBottom: 4 }} className="font-semibold dark:!text-[var(--text-secondary)]">{label}</div>
                    <div style={{ fontSize: 14, color: '#1A1A2E' }} className="dark:!text-[var(--text-primary)]">{t('kunden_detail.avg_weight')}: <span className="font-mono font-bold">{d.avg_kg.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg</span></div>
                    <div style={{ fontSize: 12, color: '#9CA3AF' }} className="dark:!text-[var(--text-tertiary)]">{d.pakete.toLocaleString(locale)} {t('kunden_detail.packages_count')} | {d.total_kg.toLocaleString(locale)} kg {t('kunden_detail.total_weight')}</div>
                  </div>
                );
              }} />
              <Area type="monotone" dataKey="avg_kg" stroke="#338edc" strokeWidth={2} fill="url(#gradWeight)" dot={false} activeDot={{ r: 6, fill: '#338edc', stroke: '#fff', strokeWidth: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ─── Card: Monats-Statistik (Billing-Daten) ──────────────────────

function CardMonatsStatistik({ kundeId }) {
  const { t, locale, tMonthShort, tMonth } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState('gesamt');
  const [ansicht, setAnsicht] = useState('monat');

  useEffect(() => {
    setLoading(true);
    api(`/api/kunden/${kundeId}/monats-statistik`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [kundeId]);

  const filteredMonate = useMemo(() => {
    if (!data?.monate) return [];
    if (yearFilter === 'gesamt') return data.monate;
    return data.monate.filter(m => m.monat.startsWith(yearFilter));
  }, [data, yearFilter]);

  const availableYears = useMemo(() => {
    if (!data?.monate?.length) return [];
    const years = [...new Set(data.monate.map(m => m.monat.split('-')[0]))].sort();
    return years;
  }, [data]);

  // Aggregate for Quartal/Jahr views
  const aggregatedData = useMemo(() => {
    if (ansicht === 'monat') return filteredMonate;
    const groups = {};
    for (const m of filteredMonate) {
      const [y, mo] = m.monat.split('-');
      let key;
      if (ansicht === 'quartal') {
        const q = Math.ceil(parseInt(mo) / 3);
        key = `${y}-Q${q}`;
      } else {
        key = y;
      }
      if (!groups[key]) groups[key] = { monat: key, pakete: 0, picks: 0, skus: 0, umsatz: 0, count: 0 };
      groups[key].pakete += m.pakete;
      groups[key].picks += m.picks;
      groups[key].skus += m.skus;
      groups[key].umsatz += m.umsatz;
      groups[key].count++;
    }
    return Object.values(groups).sort((a, b) => a.monat.localeCompare(b.monat));
  }, [filteredMonate, ansicht]);

  const chartData = useMemo(() => {
    return aggregatedData.map(m => {
      let label;
      if (ansicht === 'monat') {
        const [y, mo] = m.monat.split('-');
        label = tMonthShort(parseInt(mo) - 1) + ' ' + y.slice(2);
      } else {
        label = m.monat;
      }
      return { ...m, label };
    });
  }, [aggregatedData, ansicht, tMonthShort]);

  const fmtE = (n) => fmtEur(n, locale);

  if (loading) return <CardSkeleton className="col-span-full" />;
  if (!data || !data.monate || data.monate.length === 0) {
    return (
      <div className="bg-surface-card border border-line rounded-card p-6 col-span-full">
        <CardHeader icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2"><path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/></svg>} title={t('kunden_detail.monthly_stats_billing')} />
        <div className="text-sm text-[var(--text-tertiary)] py-4 text-center">{t('kunden_detail.no_billing_data')}</div>
      </div>
    );
  }

  const s = data.summary || {};
  const fmtNum = n => n != null ? n.toLocaleString(locale) : '–';
  const fmtPct = n => n != null ? `${n > 0 ? '+' : ''}${n}%` : '–';

  return (
    <div className="bg-surface-card border border-line rounded-card p-6 col-span-full">
      <CardHeader
        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2"><path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/></svg>}
        title={t('kunden_detail.monthly_stats_billing')}
      >
        <span className="text-xs text-content-tertiary">{t('kunden_detail.months_count', { count: data.monate.length })}</span>
      </CardHeader>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-[14px] mb-5">
        <div className="bg-surface-inset rounded-xl px-5 py-4">
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">{t('kunden_detail.avg_packages_month')}</div>
          <div className="text-[18px] font-bold text-content mt-1">{fmtNum(s.avg_pakete_6m)}</div>
          <div className="text-[11px] text-[var(--text-tertiary)]">{t('kunden_detail.last_6m')}</div>
        </div>
        <div className="bg-surface-inset rounded-xl px-5 py-4">
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">{t('kunden_detail.avg_picks_month')}</div>
          <div className="text-[18px] font-bold text-content mt-1">{fmtNum(s.avg_picks_6m)}</div>
          <div className="text-[11px] text-[var(--text-tertiary)]">{t('kunden_detail.last_6m')}</div>
        </div>
        <div className="bg-surface-inset rounded-xl px-5 py-4">
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">{t('kunden_detail.avg_skus_month')}</div>
          <div className="text-[18px] font-bold text-content mt-1">{fmtNum(s.avg_skus_6m)}</div>
          <div className="text-[11px] text-[var(--text-tertiary)]">{t('kunden_detail.last_6m')}</div>
        </div>
        <div className="bg-surface-inset rounded-xl px-5 py-4">
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">{t('kunden_detail.trend_yoy')}</div>
          <div className="text-[18px] font-bold text-content mt-1">
            {s.trend_pct != null ? fmtPct(s.trend_pct) : '–'}
          </div>
          <div className="text-[11px] text-[var(--text-tertiary)]">{t('kunden_detail.twelve_m_vs_prev')}</div>
        </div>
        <div className="bg-surface-inset rounded-xl px-5 py-4">
          <div className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">{t('kunden_detail.peak_month')}</div>
          <div className="text-[18px] font-bold text-content mt-1">{s.peak_month || '–'}</div>
          <div className="text-[11px] text-[var(--text-tertiary)]">{t('kunden_detail.highest_volume')}</div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="flex items-center gap-2.5 mb-4 flex-wrap">
        <div className="flex bg-surface-inset rounded-[10px] p-[3px] border border-line gap-0.5">
          {['gesamt', ...availableYears].map(y => (
            <button key={y} onClick={() => setYearFilter(y)}
              className={`px-3.5 py-[5px] rounded-lg text-[13px] transition-colors ${yearFilter === y ? 'bg-surface-card text-content font-semibold shadow-sm' : 'text-content-secondary hover:text-content'}`}>
              {y === 'gesamt' ? t('kunden_detail.total_filter') : y}
            </button>
          ))}
        </div>
        <div className="flex bg-surface-inset rounded-[10px] p-[3px] border border-line gap-0.5">
          {[{ k: 'monat', l: t('kunden_detail.view_month') }, { k: 'quartal', l: t('kunden_detail.view_quarter') }, { k: 'jahr', l: t('kunden_detail.view_year') }].map(v => (
            <button key={v.k} onClick={() => setAnsicht(v.k)}
              className={`px-3.5 py-[5px] rounded-lg text-[13px] transition-colors ${ansicht === v.k ? 'bg-surface-card text-content font-semibold shadow-sm' : 'text-content-secondary hover:text-content'}`}>
              {v.l}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      {chartData.length >= 2 && (
        <div className="h-[220px] mb-5">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} interval={chartData.length > 18 ? 2 : 0} angle={chartData.length > 12 ? -45 : 0} textAnchor={chartData.length > 12 ? 'end' : 'middle'} height={chartData.length > 12 ? 50 : 30} />
              <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={50} tickFormatter={v => v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={50} tickFormatter={v => v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v} />
              <Tooltip content={<ChartTooltip locale={locale} />} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="left" dataKey="pakete" name={t('kunden_detail.col_packages')} fill="#338edc" radius={[4, 4, 0, 0]} barSize={24} />
              <Line yAxisId="right" dataKey="picks" name={t('kunden_detail.picks_legend')} stroke="#22D3EE" strokeWidth={2} dot={false} activeDot={{ r: 6, fill: '#22D3EE', stroke: '#fff', strokeWidth: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Data Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-line-subtle">
              <th className="text-left py-2.5 px-3 text-[var(--text-secondary)] font-semibold uppercase text-[11px] tracking-wide">{ansicht === 'monat' ? t('kunden_detail.view_month') : ansicht === 'quartal' ? t('kunden_detail.view_quarter') : t('kunden_detail.view_year')}</th>
              <th className="text-right py-2.5 px-3 text-[var(--text-secondary)] font-semibold uppercase text-[11px] tracking-wide">{t('kunden_detail.col_packages')}</th>
              <th className="text-right py-2.5 px-3 text-[var(--text-secondary)] font-semibold uppercase text-[11px] tracking-wide">{t('kunden_detail.col_delta')}</th>
              <th className="text-right py-2.5 px-3 text-[var(--text-secondary)] font-semibold uppercase text-[11px] tracking-wide">{t('kunden_detail.col_picks')}</th>
              <th className="text-right py-2.5 px-3 text-[var(--text-secondary)] font-semibold uppercase text-[11px] tracking-wide">{t('kunden_detail.col_skus')}</th>
              <th className="text-right py-2.5 px-3 text-[var(--text-secondary)] font-semibold uppercase text-[11px] tracking-wide">{t('kunden_detail.col_avg_picks_pkg')}</th>
              <th className="text-right py-2.5 px-3 text-[var(--text-secondary)] font-semibold uppercase text-[11px] tracking-wide">{t('kunden_detail.col_revenue')}</th>
            </tr>
          </thead>
          <tbody>
            {aggregatedData.map((m, i) => {
              const prev = i > 0 ? aggregatedData[i - 1] : null;
              const delta = prev && prev.pakete > 0 ? Math.round(((m.pakete - prev.pakete) / prev.pakete) * 100) : null;
              const avgPP = m.pakete > 0 ? (m.picks / m.pakete).toFixed(1) : '–';
              let labelDisplay;
              if (ansicht === 'monat') {
                const [y, mo] = m.monat.split('-');
                labelDisplay = tMonth(parseInt(mo) - 1) + ' ' + y;
              } else {
                labelDisplay = m.monat;
              }
              return (
                <tr key={m.monat} className="border-b border-line-subtle hover:bg-surface-hover transition-colors">
                  <td className="py-2.5 px-3 text-[13px] text-content-secondary">{labelDisplay}</td>
                  <td className="py-2.5 px-3 text-right text-[13px] text-content">{fmtNum(m.pakete)}</td>
                  <td className="py-2.5 px-3 text-right">
                    {delta !== null ? (
                      <span className="text-[13px] font-medium text-content">
                        {delta > 0 ? '+' : ''}{delta}%
                      </span>
                    ) : <span className="text-[var(--text-tertiary)]">–</span>}
                  </td>
                  <td className="py-2.5 px-3 text-right text-[13px] text-content">{fmtNum(m.picks)}</td>
                  <td className="py-2.5 px-3 text-right text-[13px] text-content">{fmtNum(m.skus)}</td>
                  <td className="py-2.5 px-3 text-right text-[13px] text-content-secondary">{avgPP}</td>
                  <td className="py-2.5 px-3 text-right text-[13px] text-content">{fmtE(m.umsatz)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Card: Rechnungen (fixed — real Odoo invoices) ───────────────

function CardRechnungen({ kundeId }) {
  const { t, locale } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api(`/api/kunden/${kundeId}/rechnungen?limit=10`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [kundeId]);

  const fmtE = (n) => fmtEur(n, locale);

  // Map API status strings to translation keys
  const statusMap = { 'Bezahlt': 'kunden_detail.status_paid', 'Überfällig': 'kunden_detail.status_overdue', 'Storniert': 'kunden_detail.status_cancelled' };
  const translateStatus = (s) => t(statusMap[s] || 'kunden_detail.status_open');

  if (loading) return <CardSkeleton />;
  if (!data || !data.rechnungen || data.rechnungen.length === 0) {
    return (
      <div className="bg-surface-card border border-line rounded-card p-6">
        <div className="flex items-center gap-2 mb-2"><div className="w-[3px] h-[18px] rounded-[2px] bg-[#338edc] shrink-0" /><div className="text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('kunden_detail.invoices_title')}</div></div>
        <div className="text-sm text-[var(--text-tertiary)] py-4 text-center">{data?.hinweis || t('kunden_detail.no_invoices')}</div>
      </div>
    );
  }

  return (
    <div className="bg-surface-card border border-line rounded-card p-6">
      <div className="flex items-center gap-2 mb-3"><div className="w-[3px] h-[18px] rounded-[2px] bg-[#338edc] shrink-0" /><div className="text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('kunden_detail.invoices_title')}</div></div>
      <div className="bg-surface rounded-card border border-line overflow-hidden max-h-[280px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-surface">
            <tr className="border-b border-line text-left">
              <th className="px-3 py-2 text-[10px] font-medium text-[var(--text-secondary)] uppercase">{t('kunden_detail.col_nr')}</th>
              <th className="px-3 py-2 text-[10px] font-medium text-[var(--text-secondary)] uppercase">{t('kunden_detail.col_date')}</th>
              <th className="px-3 py-2 text-[10px] font-medium text-[var(--text-secondary)] uppercase text-right">{t('kunden_detail.col_amount')}</th>
              <th className="px-3 py-2 text-[10px] font-medium text-[var(--text-secondary)] uppercase text-right">{t('kunden_detail.col_open')}</th>
              <th className="px-3 py-2 text-[10px] font-medium text-[var(--text-secondary)] uppercase text-right">{t('kunden_detail.col_status')}</th>
            </tr>
          </thead>
          <tbody>
            {data.rechnungen.map(r => (
              <tr key={r.id} className="border-b border-line hover:bg-surface-card transition-colors">
                <td className="px-3 py-1.5 font-mono text-xs text-content-secondary">{r.nummer}</td>
                <td className="px-3 py-1.5 text-xs text-[var(--text-secondary)]">{r.datum || '–'}</td>
                <td className="px-3 py-1.5 font-mono text-xs text-right text-[var(--text-primary)]">{fmtE(r.betrag)}</td>
                <td className="px-3 py-1.5 font-mono text-xs text-right text-content-secondary">{r.offen > 0 ? fmtE(r.offen) : '–'}</td>
                <td className="px-3 py-1.5 text-right">
                  <Badge variant={r.status === 'Bezahlt' ? 'success' : r.status === 'Überfällig' ? 'error' : r.status === 'Storniert' ? 'default' : 'warning'} className="text-[9px]">{translateStatus(r.status)}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Card: Offene Cases ─────────────────────────────────────────

function CardCases({ kundeId, odooPartnerId }) {
  const { t } = useLanguage();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!odooPartnerId) { setLoading(false); return; }
    api(`/api/cases?partner=${odooPartnerId}`)
      .then(data => setCases(data.tasks || []))
      .catch(() => setCases([]))
      .finally(() => setLoading(false));
  }, [odooPartnerId]);

  if (loading) return <CardSkeleton />;
  if (!odooPartnerId) {
    return (
      <div className="bg-surface-card border border-line rounded-card p-6">
        <div className="flex items-center gap-2 mb-2"><div className="w-[3px] h-[18px] rounded-[2px] bg-[#338edc] shrink-0" /><div className="text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('kunden_detail.open_cases')}</div></div>
        <div className="text-sm text-[var(--text-tertiary)] py-4 text-center">{t('kunden_detail.no_odoo_id')}</div>
      </div>
    );
  }

  const openCases = cases.filter(c => c.stage !== 'Gelöst' && c.stage !== 'Abgebrochen');

  return (
    <div className="bg-surface-card border border-line rounded-card p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('kunden_detail.open_cases')}</span>
        </div>
        {openCases.length > 0 && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500/15 text-red-400">{openCases.length}</span>
        )}
      </div>
      {openCases.length === 0 ? (
        <div className="text-sm text-[var(--text-tertiary)] py-4 text-center">{t('kunden_detail.no_open_cases')}</div>
      ) : (
        <div className="space-y-2 max-h-[240px] overflow-y-auto">
          {openCases.slice(0, 10).map(c => (
            <div key={c.id} className="bg-surface rounded-card p-2.5 border border-line">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-content truncate flex-1 mr-2">{c.name || `Case #${c.id}`}</span>
                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-surface-inset text-content-tertiary whitespace-nowrap">{c.stage}</span>
              </div>
              {c.priority && (
                <div className="flex items-center gap-1">
                  {c.priority === '1' && <span className="text-[9px] text-yellow-400">{t('kunden_detail.priority_high')}</span>}
                  {c.priority === '2' && <span className="text-[9px] text-red-400">{t('kunden_detail.priority_urgent')}</span>}
                  {c.messages_count > 0 && <span className="text-[9px] text-[var(--text-tertiary)] ml-auto">{c.messages_count} {t('kunden_detail.messages_short')}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {cases.length > 0 && (
        <NavLink to={`/cases?kunde=${kundeId}`} className="block mt-2 text-[10px] text-forecast hover:underline text-center">{t('kunden_detail.all_cases')} &rarr;</NavLink>
      )}
    </div>
  );
}

// ─── Card: Zufriedenheit ────────────────────────────────────────

function CardZufriedenheit({ kundeId }) {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`/api/kunden/${kundeId}/zufriedenheit`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.ok) setData(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [kundeId]);

  if (loading) return <CardSkeleton />;
  if (!data || data.historie?.length === 0) return null;

  const trendIcon = data.trend === 'up' ? '\u2191' : data.trend === 'down' ? '\u2193' : '\u2192';
  const trendCls = data.trend === 'up' ? 'text-green-500' : data.trend === 'down' ? 'text-red-500' : 'text-[var(--text-secondary)]';
  const lastEntry = data.historie?.[0];
  const monatLabel = lastEntry?.monat ? (() => {
    const [y, m] = lastEntry.monat.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(m) - 1]} ${y}`;
  })() : '';

  return (
    <NavLink to="/zufriedenheit" className="block bg-surface-card border border-line rounded-card p-6 hover:border-[#338edc]/30 transition-colors">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-[3px] h-[18px] rounded-[2px] bg-[#338edc] shrink-0" />
        <span className="text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('survey.kundenDetail.title')}</span>
      </div>
      <div className="grid grid-cols-3 gap-4 mb-3">
        <div>
          <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">{t('survey.kpi.avgScore')}</p>
          <p className="text-xl font-bold text-[var(--text-primary)] tabular-nums">{data.letzter_score != null ? `${data.letzter_score} / 5` : '\u2014'}</p>
        </div>
        <div>
          <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">NPS</p>
          <p className="text-xl font-bold text-[var(--text-primary)] tabular-nums">{data.nps != null ? `${data.nps}/10` : '\u2014'}</p>
        </div>
        <div>
          <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Trend</p>
          <p className={`text-xl font-bold tabular-nums ${trendCls}`}>{trendIcon}</p>
        </div>
      </div>
      {monatLabel && (
        <p className="text-[10px] text-[var(--text-secondary)]">{t('survey.kundenDetail.lastResponse')}: {monatLabel}</p>
      )}
      {lastEntry?.feedback && (
        <p className="text-xs text-[var(--text-secondary)] mt-1 italic line-clamp-2">&ldquo;{lastEntry.feedback}&rdquo;</p>
      )}
    </NavLink>
  );
}

// ─── Kunden E-Mail-Adressen Card ─────────────────────────────────

function CardEmailAdressen({ kundeId }) {
  const { t } = useLanguage();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', rolle: '', is_primary: false, notify_on_case: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadEmails = useCallback(() => {
    fetch(`/api/kunden/${kundeId}/emails`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setEmails(d.emails || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [kundeId]);

  useEffect(() => { loadEmails(); }, [loadEmails]);

  const resetForm = () => { setForm({ email: '', name: '', rolle: '', is_primary: false, notify_on_case: true }); setEditId(null); setShowForm(false); setError(''); };

  const startEdit = (e) => {
    setForm({ email: e.email, name: e.name || '', rolle: e.rolle || '', is_primary: e.is_primary, notify_on_case: e.notify_on_case });
    setEditId(e.id);
    setShowForm(true);
    setError('');
  };

  const handleSave = async () => {
    if (!form.email.trim()) { setError(t('kundenEmails.email') + ' required'); return; }
    setSaving(true);
    setError('');
    try {
      const method = editId ? 'PUT' : 'POST';
      const url = editId ? `/api/kunden/${kundeId}/emails/${editId}` : `/api/kunden/${kundeId}/emails`;
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Error'); return; }
      resetForm();
      loadEmails();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (emailId) => {
    if (!confirm(t('kundenEmails.confirmDelete'))) return;
    try {
      const res = await fetch(`/api/kunden/${kundeId}/emails/${emailId}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Error'); return; }
      loadEmails();
    } catch (e) { alert(e.message); }
  };

  if (loading) return <CardSkeleton />;

  return (
    <div className="bg-surface-card border border-line rounded-card p-6">
      <CardHeader
        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>}
        title={t('kundenEmails.title')}
      >
        <button onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold text-primary-500 border border-primary-500/30 hover:bg-primary-500/10 transition-colors">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          {t('kundenEmails.add')}
        </button>
      </CardHeader>

      {emails.length === 0 && !showForm && (
        <div className="text-center text-content-tertiary text-xs py-4">{t('kundenEmails.noEmails')}</div>
      )}

      {emails.length > 0 && (
        <div className="space-y-1.5">
          {emails.map(e => (
            <div key={e.id} className="flex items-center gap-3 py-2 px-3 rounded-lg border border-line hover:border-line-hover transition-colors group">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[var(--text-primary)] truncate">{e.email}</span>
                  {e.is_primary && (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                      {t('kundenEmails.primary')}
                    </span>
                  )}
                  {e.notify_on_case && (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400" title={t('kundenEmails.notifyOnCase')}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline -mt-px"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>
                    </span>
                  )}
                </div>
                {(e.name || e.rolle) && (
                  <div className="text-[11px] text-[var(--text-secondary)] mt-0.5">
                    {e.name}{e.name && e.rolle ? ' · ' : ''}{e.rolle}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => startEdit(e)} className="p-1.5 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] hover:text-content transition-colors" title="Edit">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                </button>
                <button onClick={() => handleDelete(e.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-[var(--text-secondary)] hover:text-red-400 transition-colors" title="Delete">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="mt-3 p-3 rounded-lg border border-line bg-surface-inset space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              placeholder={t('kundenEmails.email')}
              className="col-span-2 w-full bg-surface-card border border-line rounded-lg px-3 py-2 text-sm text-content focus:outline-none focus:ring-1 focus:ring-primary-500/30"
            />
            <input
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder={t('kundenEmails.name')}
              className="w-full bg-surface-card border border-line rounded-lg px-3 py-2 text-sm text-content focus:outline-none focus:ring-1 focus:ring-primary-500/30"
            />
            <input
              value={form.rolle}
              onChange={e => setForm(p => ({ ...p, rolle: e.target.value }))}
              placeholder={t('kundenEmails.rollePlaceholder')}
              className="w-full bg-surface-card border border-line rounded-lg px-3 py-2 text-sm text-content focus:outline-none focus:ring-1 focus:ring-primary-500/30"
            />
          </div>
          <div className="flex items-center gap-4 text-xs text-[var(--text-secondary)]">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={form.is_primary} onChange={e => setForm(p => ({ ...p, is_primary: e.target.checked }))}
                className="w-3.5 h-3.5 rounded border-[var(--border-default,#E5E7EB)] text-primary-500 focus:ring-primary-500" />
              {t('kundenEmails.primary')}
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={form.notify_on_case} onChange={e => setForm(p => ({ ...p, notify_on_case: e.target.checked }))}
                className="w-3.5 h-3.5 rounded border-[var(--border-default,#E5E7EB)] text-primary-500 focus:ring-primary-500" />
              {t('kundenEmails.notifyOnCase')}
            </label>
          </div>
          {error && <div className="text-xs text-red-400">{error}</div>}
          <div className="flex items-center gap-2">
            <button onClick={handleSave} disabled={saving}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#338edc] hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
              {saving ? '...' : editId ? t('common.save') : t('kundenEmails.add')}
            </button>
            <button onClick={resetForm}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-[var(--text-secondary)] border border-line hover:border-line-hover transition-colors">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Portal-Zugang Card ──────────────────────────────────────────

function CardPortalZugang({ kundeId }) {
  const { t, locale } = useLanguage();
  const { user: authUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!kundeId || !['admin', 'manager'].includes(authUser?.rolle)) { setLoading(false); return; }
    fetch(`/api/kunden/${kundeId}/portal-zugang`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [kundeId, authUser?.rolle]);

  if (!['admin', 'manager'].includes(authUser?.rolle)) return null;
  if (loading) return <CardSkeleton />;

  const u = data?.user;
  const invite = data?.pending_invite;

  return (
    <div className="bg-surface-card border border-line rounded-card p-6">
      <CardHeader
        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>}
        title={t('kunden_detail.portal_access')}
      />
      {u ? (
        <div className="space-y-2">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-primary-600 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-white">{(u.vorname?.[0] || '') + (u.nachname?.[0] || '')}</span>
            </div>
            <div>
              <div className="text-xs font-medium text-content">{u.vorname} {u.nachname}</div>
              <div className="text-[10px] text-[var(--text-secondary)]">{u.email}</div>
            </div>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-[var(--text-secondary)]">{t('kunden_detail.status_label')}</span>
            <span className={`inline-flex items-center gap-1 ${u.aktiv ? 'text-emerald-400' : 'text-red-400'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${u.aktiv ? 'bg-emerald-400' : 'bg-red-400'}`} />
              {u.aktiv ? t('kunden_detail.active_status') : t('kunden_detail.deactivated')}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-[var(--text-secondary)]">{t('kunden_detail.last_login')}</span>
            <span className="text-content-secondary">{u.letzter_login ? new Date(u.letzter_login).toLocaleString(locale, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : t('kunden_detail.never')}</span>
          </div>
          {u.aktiv && ['admin', 'administrator'].includes(authUser?.rolle) && (
            <button onClick={async () => {
              try {
                const res = await fetch(`/api/admin/impersonate/${u.id}`, { method: 'POST', credentials: 'include' });
                const d = await res.json();
                if (res.ok) { await checkAuth(); navigate(d.redirect || '/portal'); }
                else alert(d.error || 'Fehler');
              } catch { alert('Netzwerkfehler'); }
            }}
              className="w-full mt-2 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              {t('userAdmin.impersonate')}
            </button>
          )}
        </div>
      ) : invite ? (
        <div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-500/10 text-yellow-400 text-[10px] font-medium mb-2">
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
            {t('kunden_detail.invite_pending')}
          </div>
          <div className="flex items-center justify-between text-xs mt-1">
            <span className="text-[var(--text-secondary)]">{t('kunden_detail.email_label')}</span>
            <span className="text-content-secondary">{invite.email}</span>
          </div>
          <div className="flex items-center justify-between text-xs mt-1">
            <span className="text-[var(--text-secondary)]">{t('kunden_detail.expires')}</span>
            <span className="text-content-secondary">{new Date(invite.laeuft_ab_am).toLocaleDateString(locale)}</span>
          </div>
        </div>
      ) : (
        <div className="text-center py-3">
          <svg className="w-7 h-7 mx-auto text-[var(--text-tertiary)] mb-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          <p className="text-xs text-[var(--text-secondary)] mb-1.5">{t('kunden_detail.no_portal_access')}</p>
          <a href="/admin/users" className="text-[10px] text-primary-400 hover:text-primary-300 transition-colors">{t('kunden_detail.link_in_user_mgmt')} &rarr;</a>
        </div>
      )}
    </div>
  );
}

// ─── Segment Override Card ───────────────────────────────────────

function CardSegment({ kunde, onUpdated }) {
  const { t, locale } = useLanguage();
  const { user: authUser } = useAuth();
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!kunde) return null;

  const isAdmin = authUser?.rolle === 'admin';
  const segColors = { A: '#338edc', B: '#4D9CE0', C: '#F59E0B' };
  const segBgMap = { A: 'bg-primary-500/10 text-primary-400', B: 'bg-purple-500/10 text-purple-400', C: 'bg-amber-500/10 text-amber-400' };

  const handleOverride = async (seg) => {
    if (seg === kunde.segment) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/kunden/${kunde.id}/segment-override`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ segment: seg }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setOverrideOpen(false);
      if (onUpdated) onUpdated();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleRemoveOverride = async () => {
    setSaving(true);
    try {
      await fetch(`/api/kunden/${kunde.id}/segment-override`, { method: 'DELETE', credentials: 'include' });
      if (onUpdated) onUpdated();
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <div className="bg-surface-card border border-line rounded-card p-6">
      <CardHeader
        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></svg>}
        title={t('kunden_detail.segment_title')}
      />
      <div className="flex items-center gap-3 mb-2">
        <span className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-extrabold" style={{ backgroundColor: (segColors[kunde.segment] || '#6b7280') + '20', color: segColors[kunde.segment] || '#6b7280' }}>
          {kunde.segment}
        </span>
        <div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${segBgMap[kunde.segment] || 'bg-surface-inset text-content-tertiary'}`}>
              {kunde.segment}{t('kunden_detail.customer_suffix')}
            </span>
            {kunde.segment_manuell && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/10 text-amber-400">{t('kunden_detail.manual_label')}</span>}
          </div>
          {kunde.segment_aktualisiert_am && (
            <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5">{t('kunden_detail.since_date', { date: new Date(kunde.segment_aktualisiert_am).toLocaleDateString(locale) })}</div>
          )}
        </div>
      </div>
      <div className="text-xs text-[var(--text-secondary)] mb-1">{t('kunden_detail.picking_method', { method: kunde.segment === 'C' ? t('kunden_detail.mixed_wave_method') : t('kunden_detail.customer_specific') })}</div>
      {isAdmin && (
        <div className="mt-2 pt-2 border-t border-line/50">
          {!overrideOpen ? (
            <div className="flex items-center gap-2">
              <button onClick={() => setOverrideOpen(true)} className="text-[10px] text-primary-400 hover:text-primary-300 transition-colors">{t('kunden_detail.change_segment')}</button>
              {kunde.segment_manuell && (
                <button onClick={handleRemoveOverride} disabled={saving} className="text-[10px] text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] transition-colors">{t('kunden_detail.remove_override')}</button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {['A', 'B', 'C'].map(s => (
                <button
                  key={s}
                  onClick={() => handleOverride(s)}
                  disabled={saving || s === kunde.segment}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                    s === kunde.segment ? 'bg-surface-inset text-[var(--text-tertiary)] cursor-not-allowed' : 'hover:opacity-80'
                  }`}
                  style={s !== kunde.segment ? { backgroundColor: segColors[s] + '20', color: segColors[s] } : {}}
                >
                  {s}
                </button>
              ))}
              <button onClick={() => setOverrideOpen(false)} className="text-[10px] text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] ml-1">{t('common.cancel')}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── SLA-Klasse Card ─────────────────────────────────────────────

const SLA_FARBE_CLS = {
  gray:   'bg-gray-500/10 text-[var(--text-secondary,#4B5563)]',
  violet: 'bg-primary-500/10 text-primary-500',
  orange: 'bg-orange-500/10 text-orange-500',
  red:    'bg-red-500/10 text-red-500',
  green:  'bg-green-500/10 text-green-500',
  blue:   'bg-blue-500/10 text-blue-500',
};

function CardSlaKlasse({ kunde, onUpdated }) {
  const { t } = useLanguage();
  const { user: authUser } = useAuth();
  const [klassen, setKlassen] = useState([]);
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState('');
  const [overrideH, setOverrideH] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch('/api/sla-klassen', { credentials: 'include' })
      .then(r => r.json()).then(d => { setKlassen(d.klassen || (Array.isArray(d) ? d : [])); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (kunde) {
      setSelected(kunde.sla_klasse_id ? String(kunde.sla_klasse_id) : '');
      setOverrideH(kunde.sla_stunden != null ? String(kunde.sla_stunden) : '');
    }
  }, [kunde]);

  if (!kunde) return null;
  const isAdmin = authUser?.rolle === 'admin';
  const current = klassen.find(k => k.id === kunde.sla_klasse_id);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/kunden/${kunde.id}/sla`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sla_klasse_id: selected ? Number(selected) : null,
          sla_override_stunden: overrideH ? Number(overrideH) : null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setEditing(false);
      if (onUpdated) onUpdated();
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <div className="bg-surface-card border border-line rounded-card p-6">
      <CardHeader
        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>}
        title={t('sla.title')}
      />

      {/* Current SLA display */}
      <div className="flex items-center gap-3 mb-2">
        {current ? (
          <>
            <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${SLA_FARBE_CLS[current.farbe] || SLA_FARBE_CLS.gray}`}>
              {current.icon && <span className="mr-1">{current.icon}</span>}
              {current.label || current.name}
            </span>
            <span className="text-xs text-[var(--text-secondary)]">{current.stunden}h {t('sla.stunden')}</span>
          </>
        ) : (
          <span className="text-xs text-[var(--text-tertiary)] italic">{t('sla.noKlasse')}</span>
        )}
        {kunde.sla_stunden != null && (
          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/10 text-amber-400">
            Override: {kunde.sla_stunden}h
          </span>
        )}
      </div>

      {/* Admin edit section */}
      {isAdmin && (
        <div className="mt-2 pt-2 border-t border-line/50">
          {!editing ? (
            <button onClick={() => setEditing(true)} className="text-[10px] text-primary-400 hover:text-primary-300 transition-colors">
              {t('sla.bearbeiten')}
            </button>
          ) : (
            <div className="space-y-2">
              <div>
                <label className="text-[10px] text-[var(--text-tertiary)] block mb-1">{t('sla.klasseLabel')}</label>
                <select
                  value={selected}
                  onChange={e => setSelected(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs rounded-lg border border-line bg-surface-card text-content focus:outline-none focus:ring-1 focus:ring-primary-500/50"
                >
                  <option value="">{t('sla.noKlasse')}</option>
                  {klassen.filter(k => k.aktiv !== false).map(k => (
                    <option key={k.id} value={k.id}>
                      {k.icon ? `${k.icon} ` : ''}{k.label || k.name} ({k.stunden}h)
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-[var(--text-tertiary)] block mb-1">{t('sla.override')}</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={overrideH}
                  onChange={e => setOverrideH(e.target.value)}
                  placeholder={t('sla.overrideHint')}
                  className="w-full px-2 py-1.5 text-xs rounded-lg border border-line bg-surface-card text-content focus:outline-none focus:ring-1 focus:ring-primary-500/50"
                />
              </div>
              <div className="flex items-center gap-2">
                <button onClick={handleSave} disabled={saving} className="px-3 py-1 text-[10px] font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-500 disabled:opacity-50 transition-colors">
                  {t('common.save')}
                </button>
                <button onClick={() => { setEditing(false); setSelected(kunde.sla_klasse_id ? String(kunde.sla_klasse_id) : ''); setOverrideH(kunde.sla_stunden != null ? String(kunde.sla_stunden) : ''); }} className="text-[10px] text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]">
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Hotline-Kennwort Card (#1806) ───────────────────────────────
// 4-stelliges Kunden-Kennwort für die Telefon-Verifikation. Wird nur beim
// Setzen im Eingabefeld angezeigt, danach ist nur der Status (gesetzt/nicht
// gesetzt) sichtbar — der Server speichert ausschließlich einen Hash.

function CardHotlineKennwort({ kundeId }) {
  const { t, locale } = useLanguage();
  const { user: authUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [editing, setEditing] = useState(false);
  const [kennwort, setKennwort] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canManage = ['admin', 'administrator', 'manager', 'ops_manager'].includes(authUser?.rolle);

  const loadStatus = () => {
    fetch(`/api/kunden/${kundeId}/hotline-kennwort`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null)).then(d => { if (d) setStatus(d); }).catch(() => {});
  };
  useEffect(() => { if (canManage) loadStatus(); }, [kundeId, canManage]);

  if (!canManage) return null;

  const handleSave = async () => {
    setError('');
    if (!/^\d{4}$/.test(kennwort)) { setError(t('kunden_detail.kennwort.formatError')); return; }
    setSaving(true);
    try {
      const res = await fetch(`/api/kunden/${kundeId}/hotline-kennwort`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ kennwort }),
      });
      if (!res.ok) throw new Error();
      setEditing(false); setKennwort('');
      loadStatus();
    } catch { setError(t('kunden_detail.kennwort.saveError')); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(t('kunden_detail.kennwort.deleteConfirm'))) return;
    setSaving(true);
    try {
      await fetch(`/api/kunden/${kundeId}/hotline-kennwort`, { method: 'DELETE', credentials: 'include' });
      loadStatus();
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <div className="bg-surface-card border border-line rounded-card p-6">
      <CardHeader
        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="10" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>}
        title={t('kunden_detail.kennwort.title')}
      />
      <div className="text-xs text-[var(--text-secondary)] mb-3">{t('kunden_detail.kennwort.hint')}</div>
      <div className="flex items-center gap-2 mb-2">
        {status?.gesetzt ? (
          <>
            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-green-500/10 text-green-500">{t('kunden_detail.kennwort.gesetzt')}</span>
            {status.gesetzt_am && (
              <span className="text-[10px] text-[var(--text-tertiary)]">
                {t('kunden_detail.kennwort.gesetztAm', { date: new Date(status.gesetzt_am).toLocaleDateString(locale) })}
                {status.gesetzt_von ? ` · ${status.gesetzt_von}` : ''}
              </span>
            )}
          </>
        ) : (
          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-surface-inset text-content-tertiary">{t('kunden_detail.kennwort.nichtGesetzt')}</span>
        )}
      </div>
      {!editing ? (
        <div className="flex items-center gap-3">
          <button onClick={() => { setEditing(true); setKennwort(''); setError(''); }} className="text-[10px] text-primary-400 hover:text-primary-300 transition-colors">
            {status?.gesetzt ? t('kunden_detail.kennwort.neuSetzen') : t('kunden_detail.kennwort.setzen')}
          </button>
          {status?.gesetzt && (
            <button onClick={handleDelete} disabled={saving} className="text-[10px] text-red-400 hover:text-red-300 transition-colors disabled:opacity-50">
              {t('kunden_detail.kennwort.entfernen')}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <input
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={kennwort}
            onChange={e => setKennwort(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="0000"
            className="w-24 px-2 py-1.5 text-sm tracking-[0.3em] text-center rounded-lg border border-line bg-surface-card text-content focus:outline-none focus:ring-1 focus:ring-primary-500/50"
            autoFocus
          />
          {error && <div className="text-[10px] text-red-400">{error}</div>}
          <div className="flex items-center gap-2">
            <button onClick={handleSave} disabled={saving || kennwort.length !== 4} className="px-3 py-1 text-[10px] font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-500 disabled:opacity-50 transition-colors">
              {t('common.save')}
            </button>
            <button onClick={() => { setEditing(false); setKennwort(''); setError(''); }} className="text-[10px] text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Odoo Partner Picker ─────────────────────────────────────────

function OdooPartnerPicker({ value, onChange, kundeName }) {
  const { t } = useLanguage();
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [searchName, setSearchName] = useState('');

  const doSearch = async (name) => {
    if (!name) return;
    setSearching(true);
    setResults(null);
    try {
      const data = await api(`/api/odoo/partner-search?name=${encodeURIComponent(name)}`);
      setResults(data);
    } catch { setResults({ partners: [], hinweis: t('kunden_detail.search_failed') }); }
    finally { setSearching(false); }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input type="number" value={value || ''} onChange={(e) => onChange(e.target.value)}
          placeholder={t('kunden_detail.odoo_partner_id_placeholder')}
          className="flex-1 bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content placeholder-content-dim focus:outline-none focus:border-line-hover focus:ring-1 focus:ring-primary-500/30" />
        <button type="button" onClick={() => { setSearchName(kundeName); doSearch(kundeName); }} disabled={searching || !kundeName}
          className="px-3 py-2 rounded-lg text-xs font-medium bg-cyan-600/20 text-cyan-400 hover:bg-cyan-600/30 border border-cyan-500/20 transition-colors disabled:opacity-40 whitespace-nowrap">
          {searching ? t('kunden_detail.searching') : t('kunden_detail.auto_match')}
        </button>
      </div>
      {results !== null && (
        <div className="bg-surface-card border border-line rounded-card p-3 space-y-2">
          <div className="flex gap-2">
            <input type="text" value={searchName} onChange={(e) => setSearchName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), doSearch(searchName))}
              placeholder={t('kunden_detail.search_term')}
              className="flex-1 bg-surface-inset border border-line-hover rounded px-2 py-1 text-xs text-content placeholder-content-dim focus:outline-none" />
            <button type="button" onClick={() => doSearch(searchName)} disabled={searching}
              className="px-2 py-1 text-[10px] bg-surface-inset hover:bg-surface-active rounded text-content-tertiary transition-colors">{t('kunden_detail.search_btn')}</button>
          </div>
          {results.hinweis && <div className="text-[10px] text-yellow-400">{results.hinweis}</div>}
          {results.partners?.length > 0 ? (
            <div className="space-y-1">
              {results.partners.map(p => (
                <button key={p.id} type="button" onClick={() => { onChange(String(p.id)); setResults(null); }}
                  className={`w-full text-left px-2.5 py-1.5 rounded text-xs transition-colors ${String(value) === String(p.id) ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' : 'hover:bg-surface-hover text-content-secondary'}`}>
                  <div className="flex items-center justify-between">
                    <span>{p.name}</span>
                    <span className="text-[10px] text-[var(--text-secondary)] font-mono">ID: {p.id}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : !results.hinweis && <div className="text-[10px] text-[var(--text-secondary)] py-1">{t('kunden_detail.no_results')}</div>}
          <button type="button" onClick={() => setResults(null)} className="text-[10px] text-[var(--text-tertiary)] hover:text-content-tertiary">{t('kunden_detail.close_search')}</button>
        </div>
      )}
    </div>
  );
}

// ─── Changelog / Verlauf Card ─────────────────────────────────────

const SOURCE_LABELS = {
  direct_edit: 'Manual',
  zeitvorgaben_override: 'Override',
  zeitvorgaben_accept_all: 'Accept All',
  calibrate_defaults: 'Calibrate',
};
const SOURCE_COLORS = {
  direct_edit: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  zeitvorgaben_override: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  zeitvorgaben_accept_all: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  calibrate_defaults: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
};

function CardChangelog({ kundeId }) {
  const { t } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api(`/api/kunden/${kundeId}/changelog?limit=20`);
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [kundeId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) {
    return (
      <div className="mb-5">
        <button onClick={() => setOpen(true)}
          className="w-full text-left px-4 py-3 border border-[var(--border-primary)] rounded-xl bg-[var(--bg-card)] text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)] transition-colors flex items-center justify-between">
          <span>{t('kunden_detail.changelog_title', 'Change History')}</span>
          <span className="text-xs text-[var(--text-tertiary)]">▼</span>
        </button>
      </div>
    );
  }

  return (
    <div className="mb-5 border border-[var(--border-primary)] rounded-xl bg-[var(--bg-card)] overflow-hidden">
      <button onClick={() => setOpen(false)}
        className="w-full text-left px-4 py-3 text-sm font-semibold text-[var(--text-primary)] flex items-center justify-between border-b border-[var(--border-primary)]">
        <span>{t('kunden_detail.changelog_title', 'Change History')} {total > 0 && <span className="text-xs text-[var(--text-tertiary)] font-normal ml-2">({total})</span>}</span>
        <span className="text-xs text-[var(--text-tertiary)]">▲</span>
      </button>
      <div className="px-4 py-3 max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('common.loading', 'Loading...')}</div>
        ) : entries.length === 0 ? (
          <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('kunden_detail.changelog_empty', 'No changes recorded yet.')}</div>
        ) : (
          <div className="space-y-2">
            {entries.map(e => (
              <div key={e.id} className="flex items-start gap-3 text-xs py-1.5 border-b border-[var(--border-primary)] last:border-0">
                <div className="flex-shrink-0 w-[120px] text-[var(--text-tertiary)]">
                  {new Date(e.changed_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </div>
                <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${SOURCE_COLORS[e.source] || 'bg-gray-100 text-[var(--text-secondary,#4B5563)]'}`}>
                  {SOURCE_LABELS[e.source] || e.source}
                </span>
                <div className="flex-1 min-w-0">
                  <span className="font-mono text-[var(--text-secondary)]">{e.field_name}</span>
                  <span className="text-[var(--text-tertiary)]"> : </span>
                  <span className="text-red-500 line-through">{e.old_value ?? '–'}</span>
                  <span className="text-[var(--text-tertiary)]"> → </span>
                  <span className="text-green-600 font-medium">{e.new_value ?? '–'}</span>
                </div>
                {e.changed_by && <div className="flex-shrink-0 text-[var(--text-tertiary)] truncate max-w-[100px]">{e.changed_by.split('@')[0]}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tabbed Edit Modal ────────────────────────────────────────────

function TabbedEditModal({ open, onClose, kunde, standorte, onSaved }) {
  const { t } = useLanguage();
  const [tab, setTab] = useState(0);
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const [recalcing, setRecalcing] = useState(false);

  useEffect(() => {
    if (kunde) setF({
      name: kunde.name || '',
      segment: kunde.segment || 'B',
      standort_id: kunde.standort_id || '',
      odoo_partner_id: kunde.odoo_partner_id || '',
      sync_key: kunde.sync_key || '',
      picking_time_per_paket: kunde.picking_time_per_paket || 2,
      packing_time_per_paket: kunde.packing_time_per_paket || 3,
      pick_pro_sku_min: kunde.pick_pro_sku_min || '',
      pick_pro_artikel_min: kunde.pick_pro_artikel_min || '',
      pack_basis_min: kunde.pack_basis_min || '',
      pack_pro_artikel_min: kunde.pack_pro_artikel_min || '',
      cutoff_stunde: kunde.cutoff_stunde || 12,
      vor_cutoff_pct: kunde.vor_cutoff_pct || 60,
      avg_skus_per_order: kunde.avg_skus_per_order || 1,
      avg_articles_per_order: kunde.avg_articles_per_order || 1,
      sop_anteil: (Number(kunde.sop_anteil || 0) * 100).toFixed(1),
      avg_sop_pick_zeit: kunde.avg_sop_pick_zeit || 0,
      avg_sop_pack_zeit: kunde.avg_sop_pack_zeit || 0,
    });
  }, [kunde]);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api(`/api/kunden/${kunde.id}`, {
        method: 'PUT',
        body: {
          name: f.name,
          segment: f.segment,
          standort_id: f.standort_id ? Number(f.standort_id) : null,
          odoo_partner_id: f.odoo_partner_id ? Number(f.odoo_partner_id) : null,
          sync_key: (f.sync_key || '').trim().slice(0, 100) || null,
          picking_time_per_paket: Number(f.picking_time_per_paket),
          packing_time_per_paket: Number(f.packing_time_per_paket),
          pick_pro_sku_min: f.pick_pro_sku_min !== '' ? Number(f.pick_pro_sku_min) : null,
          pick_pro_artikel_min: f.pick_pro_artikel_min !== '' ? Number(f.pick_pro_artikel_min) : null,
          pack_basis_min: f.pack_basis_min !== '' ? Number(f.pack_basis_min) : null,
          pack_pro_artikel_min: f.pack_pro_artikel_min !== '' ? Number(f.pack_pro_artikel_min) : null,
          cutoff_stunde: Number(f.cutoff_stunde),
          vor_cutoff_pct: Number(f.vor_cutoff_pct),
          avg_skus_per_order: Number(f.avg_skus_per_order),
          avg_articles_per_order: Number(f.avg_articles_per_order),
          sop_anteil: Number(f.sop_anteil) / 100,
          avg_sop_pick_zeit: Number(f.avg_sop_pick_zeit),
          avg_sop_pack_zeit: Number(f.avg_sop_pack_zeit),
        },
      });
      onSaved();
      onClose();
    } catch (e) { alert(t('kunden_detail.error_prefix', { msg: e.message })); }
    finally { setSaving(false); }
  };

  const recalculate = async () => {
    setRecalcing(true);
    try {
      await api('/api/kunden/avg-berechnen', { method: 'POST' });
      const detail = await api(`/api/kunden/${kunde.id}/detail`);
      if (detail.kunde) {
        set('avg_skus_per_order', detail.kunde.avg_skus_per_order);
        set('avg_articles_per_order', detail.kunde.avg_articles_per_order);
        set('sop_anteil', (Number(detail.kunde.sop_anteil || 0) * 100).toFixed(1));
        set('avg_sop_pick_zeit', detail.kunde.avg_sop_pick_zeit);
        set('avg_sop_pack_zeit', detail.kunde.avg_sop_pack_zeit);
      }
    } catch (e) { alert(t('kunden_detail.error_prefix', { msg: e.message })); }
    finally { setRecalcing(false); }
  };

  const tabs = [t('kunden_detail.tab_master_data'), t('kunden_detail.tab_time_targets'), t('kunden_detail.tab_profile')];
  const inp = "w-full bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content font-mono focus:outline-none focus:border-line-hover focus:ring-1 focus:ring-primary-500/30";

  return (
    <Modal open={open} onClose={onClose} title={t('kunden_detail.edit_customer_title')} wide>
      <div className="flex border-b border-line mb-4 -mt-1">
        {tabs.map((t, i) => (
          <button key={i} onClick={() => setTab(i)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${i === tab ? 'border-forecast text-forecast' : 'border-transparent text-[var(--text-secondary)] hover:text-content-secondary'}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === 0 && (
        <div className="space-y-4">
          <label className="block">
            <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.edit_name')}</span>
            <input value={f.name || ''} onChange={e => set('name', e.target.value)} className={`mt-1.5 ${inp}`} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.edit.segment')}</span>
              <select value={f.segment || 'B'} onChange={e => set('segment', e.target.value)}
                className={`mt-1.5 ${inp}`}>
                <option value="A">{t('kunden_detail.seg_a_large')}</option>
                <option value="B">{t('kunden_detail.seg_b_standard')}</option>
                <option value="C">{t('kunden_detail.seg_c_small')}</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.edit_location')}</span>
              <select value={f.standort_id || ''} onChange={e => set('standort_id', e.target.value)}
                className={`mt-1.5 ${inp}`}>
                <option value="">{t('kunden_detail.none_option')}</option>
                {standorte.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.odoo_partner_id_placeholder')}</span>
            <div className="mt-1.5">
              <OdooPartnerPicker value={f.odoo_partner_id || ''} onChange={v => set('odoo_partner_id', v)} kundeName={f.name} />
            </div>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.sync_key_label')}</span>
            <input
              type="text"
              value={f.sync_key || ''}
              onChange={e => set('sync_key', e.target.value.slice(0, 100))}
              maxLength={100}
              className={`mt-1.5 ${inp}`}
              placeholder={t('kunden_detail.sync_key_placeholder')}
            />
            <span className="block mt-1 text-[11px] text-[var(--text-tertiary)]">{t('kunden_detail.sync_key_help')}</span>
          </label>
        </div>
      )}

      {tab === 1 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.normal_pick_time')} (min)</span>
              <input type="number" step="0.1" value={f.picking_time_per_paket || ''} onChange={e => set('picking_time_per_paket', e.target.value)} className={`mt-1.5 ${inp}`} placeholder="z.B. 2.5" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.normal_pack_time')} (min)</span>
              <input type="number" step="0.1" value={f.packing_time_per_paket || ''} onChange={e => set('packing_time_per_paket', e.target.value)} className={`mt-1.5 ${inp}`} placeholder="z.B. 3.0" />
            </label>
          </div>

          {/* Regressionsbasierte Zeitvorgaben */}
          <div className="pt-2 border-t border-[var(--border-primary)]">
            <span className="text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wider">{t('kunden_detail.regression_times_section', 'Regression-based Time Standards')}</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.pick_pro_sku', 'Pick per SKU')} (min)</span>
              <input type="number" step="0.0001" value={f.pick_pro_sku_min || ''} onChange={e => set('pick_pro_sku_min', e.target.value)} className={`mt-1.5 ${inp}`} placeholder="z.B. 0.45" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.pick_pro_artikel', 'Pick per Article')} (min)</span>
              <input type="number" step="0.0001" value={f.pick_pro_artikel_min || ''} onChange={e => set('pick_pro_artikel_min', e.target.value)} className={`mt-1.5 ${inp}`} placeholder="z.B. 0.30" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.pack_basis', 'Pack Basis')} (min)</span>
              <input type="number" step="0.0001" value={f.pack_basis_min || ''} onChange={e => set('pack_basis_min', e.target.value)} className={`mt-1.5 ${inp}`} placeholder="z.B. 1.50" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.pack_pro_artikel', 'Pack per Article')} (min)</span>
              <input type="number" step="0.0001" value={f.pack_pro_artikel_min || ''} onChange={e => set('pack_pro_artikel_min', e.target.value)} className={`mt-1.5 ${inp}`} placeholder="z.B. 0.35" />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.cutoff_hour')} (h)</span>
              <input type="number" step="0.5" min="0" max="24" value={f.cutoff_stunde || ''} onChange={e => set('cutoff_stunde', e.target.value)} className={`mt-1.5 ${inp}`} placeholder="z.B. 12" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.before_cutoff_pct')} (%)</span>
              <input type="number" step="1" min="0" max="100" value={f.vor_cutoff_pct || ''} onChange={e => set('vor_cutoff_pct', e.target.value)} className={`mt-1.5 ${inp}`} placeholder="z.B. 60" />
            </label>
          </div>
        </div>
      )}

      {tab === 2 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.avg_skus_order_edit')}</span>
              <input type="number" step="0.01" value={f.avg_skus_per_order || ''} onChange={e => set('avg_skus_per_order', e.target.value)} className={`mt-1.5 ${inp}`} />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.avg_articles_order_edit')}</span>
              <input type="number" step="0.01" value={f.avg_articles_per_order || ''} onChange={e => set('avg_articles_per_order', e.target.value)} className={`mt-1.5 ${inp}`} />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.sop_share_pct')}</span>
            <input type="number" step="0.1" value={f.sop_anteil || ''} onChange={e => set('sop_anteil', e.target.value)} className={`mt-1.5 ${inp}`} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.sop_pick_time')}</span>
              <input type="number" step="0.01" value={f.avg_sop_pick_zeit || ''} onChange={e => set('avg_sop_pick_zeit', e.target.value)} className={`mt-1.5 ${inp}`} />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('kunden_detail.sop_pack_time')}</span>
              <input type="number" step="0.01" value={f.avg_sop_pack_zeit || ''} onChange={e => set('avg_sop_pack_zeit', e.target.value)} className={`mt-1.5 ${inp}`} />
            </label>
          </div>
          <button type="button" onClick={recalculate} disabled={recalcing}
            className="px-4 py-2 rounded-lg text-xs font-medium bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600/30 border border-indigo-500/20 transition-colors disabled:opacity-50 flex items-center gap-2">
            {recalcing ? (
              <><span className="animate-spin h-3 w-3 border border-indigo-400 border-t-transparent rounded-full" />{t('kunden_detail.recalculating')}</>
            ) : t('kunden_detail.recalculate')}
          </button>
        </div>
      )}

      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-line">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-content-tertiary hover:text-content hover:bg-surface-hover transition-colors">{t('common.cancel')}</button>
        <button onClick={save} disabled={saving}
          className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-surface-active text-content hover:bg-surface-active transition-colors disabled:opacity-50">
          {saving ? t('kunden_detail.edit.saving') : t('common.save')}
        </button>
      </div>
    </Modal>
  );
}

// ─── Card: Commitments / Vereinbarungen (#930) ───────────────────

function CardCommitments({ kundeId }) {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api(`/api/kunden/${kundeId}/commitments`)
      .then((d) => setItems(d.commitments || d.items || d || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [kundeId]);

  const STATUS_CLS = {
    draft:             'bg-[var(--bg-inset)] text-[var(--text-secondary)]',
    pending_internal:  'bg-amber-50 text-amber-700',
    pending_customer:  'bg-blue-50 text-blue-700',
    active:            'bg-emerald-50 text-emerald-700',
    rejected_internal: 'bg-rose-50 text-rose-700',
    rejected_customer: 'bg-rose-50 text-rose-700',
    expired:           'bg-[var(--bg-inset)] text-[var(--text-tertiary)]',
    terminated:        'bg-rose-50 text-rose-700',
    superseded:        'bg-indigo-50 text-indigo-700',
  };

  if (loading) return <CardSkeleton />;

  return (
    <div className="bg-surface-card border border-line rounded-card p-6">
      <CardHeader
        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 17a4 4 0 0 1 4-4h6"/><path d="M21 13l-3-3m0 6l3-3"/><path d="M11 17H3"/><path d="M3 7h6a4 4 0 0 1 4 4v6"/></svg>}
        title={t('commitment.card_title')}
      >
        <a href={`/commitments/new?kunde_id=${kundeId}`} className="text-xs text-[#338edc] hover:underline">+ {t('commitment.new')}</a>
      </CardHeader>

      {items.length === 0 ? (
        <div className="text-xs text-[var(--text-tertiary)] italic">{t('commitment.empty_kunde')}</div>
      ) : (
        <div className="space-y-1.5">
          {items.map((c) => (
            <a
              key={c.id}
              href={`/commitments/${c.id}`}
              className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-[var(--bg-inset)] text-sm"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono text-[10px] text-[var(--text-tertiary)]">{c.commitment_nr}</span>
                <span className="truncate text-[var(--text-primary)]">{c.titel}</span>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_CLS[c.status] || 'bg-gray-100 text-gray-700'}`}>
                {t(`commitment.status.${c.status}`) || c.status}
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Card: Aktionszeiträume ──────────────────────────────────────

function CardAktionszeitraeume({ kundeId }) {
  const { t } = useLanguage();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api(`/api/aktionszeitraeume/kunde/${kundeId}`)
      .then(setData).catch(() => setData([])).finally(() => setLoading(false));
  }, [kundeId]);

  if (loading) return <CardSkeleton />;

  const typColors = { black_week: '#EF4444', sale: '#F59E0B', launch: '#3B82F6', kampagne: '#8B5CF6', saison: '#06B6D4', sonstige: '#6B7280' };
  const typLabels = { black_week: 'Black Week', sale: 'Sale', launch: 'Launch', kampagne: 'Kampagne', saison: 'Saison', sonstige: 'Sonstige' };
  const statusColors = { eingereicht: '#D97706', genehmigt: '#338edc', abgelehnt: '#DC2626', aktiv: '#059669', abgeschlossen: '#6B7280' };
  const statusLabels = { eingereicht: t('forecastPromo.status.eingereicht'), genehmigt: t('forecastPromo.status.genehmigt'), abgelehnt: t('forecastPromo.status.abgelehnt'), aktiv: t('forecastPromo.status.aktiv'), abgeschlossen: t('forecastPromo.status.abgeschlossen') };
  const fmtD = (d) => d ? new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '–';

  return (
    <div className="bg-surface-card border border-line rounded-card p-6">
      <CardHeader
        icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>}
        title={t('forecastPromo.title')}
      >
        <NavLink to="/forecast-review?seg=aktionszeitraeume" className="text-[10px] text-forecast hover:underline">{t('forecastPromo.title')} &rarr;</NavLink>
      </CardHeader>

      {data.length === 0 ? (
        <div className="text-sm text-[var(--text-tertiary)] py-4 text-center">{t('forecastPromo.noEntries')}</div>
      ) : (
        <table className="w-full text-sm mt-3">
          <thead>
            <tr className="border-b border-line">
              <th className="text-left py-1 text-[10px] font-medium text-[var(--text-tertiary)] uppercase">{t('forecastPromo.titleField')}</th>
              <th className="text-left py-1 text-[10px] font-medium text-[var(--text-tertiary)] uppercase">{t('forecastPromo.type')}</th>
              <th className="text-left py-1 text-[10px] font-medium text-[var(--text-tertiary)] uppercase">{t('forecastPromo.from')}</th>
              <th className="text-left py-1 text-[10px] font-medium text-[var(--text-tertiary)] uppercase">{t('forecastPromo.to')}</th>
              <th className="text-right py-1 text-[10px] font-medium text-[var(--text-tertiary)] uppercase">{t('forecastPromo.multiplier')}</th>
              <th className="text-center py-1 text-[10px] font-medium text-[var(--text-tertiary)] uppercase">{t('forecastPromo.status')}</th>
              <th className="text-right py-1 text-[10px] font-medium text-[var(--text-tertiary)] uppercase">{t('forecastPromo.istVsPlan')}</th>
            </tr>
          </thead>
          <tbody>
            {data.slice(0, 10).map(a => (
              <tr key={a.id} className="border-b border-line/50">
                <td className="py-2 text-[var(--text-primary)] font-medium">{a.titel}</td>
                <td className="py-2">
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: (typColors[a.typ] || '#6B7280') + '20', color: typColors[a.typ] || '#6B7280' }}>
                    {typLabels[a.typ] || a.typ}
                  </span>
                </td>
                <td className="py-2 text-[var(--text-secondary)]">{fmtD(a.datum_von)}</td>
                <td className="py-2 text-[var(--text-secondary)]">{fmtD(a.datum_bis)}</td>
                <td className="py-2 text-right font-medium text-[var(--text-primary)]">
                  {a.multiplikator ? `${a.multiplikator}x` : `+${a.zusatz_orders}`}
                </td>
                <td className="py-2 text-center">
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: (statusColors[a.status] || '#6B7280') + '20', color: statusColors[a.status] || '#6B7280' }}>
                    {statusLabels[a.status] || a.status}
                  </span>
                </td>
                <td className="py-2 text-right text-[var(--text-secondary)]">
                  {a.bewertet && a.ist_multiplikator != null ? `${a.ist_multiplikator}x` : '–'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════

export default function KundenDetail() {
  const { t, locale, tMonthShort } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const kundeId = Number(id);

  const [detail, setDetail] = useState(null);
  const [standorte, setStandorte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [tage, setTage] = useState(30);
  const [claimsLinkCopied, setClaimsLinkCopied] = useState(false);
  const [weightKpi, setWeightKpi] = useState(null);
  const [chatterEnabled, setChatterEnabled] = useState(false);

  useEffect(() => {
    api('/api/chatter/config')
      .then(d => {
        const v = d?.config?.chatter_enabled_kunde;
        setChatterEnabled(v === true || v === 'true');
      })
      .catch(() => {});
  }, []);

  const copyClaimsLink = async () => {
    try {
      const res = await api(`/api/claims-portal/generate-token/${id}`, { method: 'POST' });
      const link = `${window.location.origin}/claims/submit?token=${res.token}`;
      await navigator.clipboard.writeText(link);
      setClaimsLinkCopied(true);
      setTimeout(() => setClaimsLinkCopied(false), 3000);
    } catch (err) {
      console.error('Claims link copy error:', err);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, st] = await Promise.all([
        api(`/api/kunden/${id}/detail?tage=${tage}`),
        api('/api/standorte'),
      ]);
      setDetail(d);
      setStandorte(st);
    } catch (e) {
      setDetail(null);
    }
    setLoading(false);
  }, [id, tage]);

  useEffect(() => { load(); }, [load]);

  // Fetch weight KPI for header
  useEffect(() => {
    api(`/api/kunden/${id}/gewicht-trend?tage=${tage}`)
      .then(d => d?.current?.pakete > 0 ? setWeightKpi(d.current) : setWeightKpi(null))
      .catch(() => setWeightKpi(null));
  }, [id, tage]);

  const kunde = detail?.kunde;
  const stats = detail?.stats;

  if (loading && !detail) {
    return (
      <div className="p-page">
        <div className="text-center py-20 text-[var(--text-tertiary)]">{t('kunden_detail.loading_customer')}</div>
      </div>
    );
  }

  if (!kunde) {
    return (
      <div className="p-page">
        <div className="text-center py-20">
          <div className="text-lg font-bold text-content-tertiary mb-2">{t('kunden_detail.customer_not_found')}</div>
          <NavLink to="/kunden" className="text-forecast hover:underline text-sm">{t('kunden_detail.back_to_customers')}</NavLink>
        </div>
      </div>
    );
  }

  const sopPct = Number(kunde.sop_anteil || 0) * 100;

  return (
    <div className="p-page">
      <BackLink to="/kunden" label={t('common.backToList')} className="mb-3" />
      {/* Header */}
      <div className="bg-surface-card border border-line rounded-card px-7 py-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-5">
            <div className="w-[52px] h-[52px] rounded-[14px] flex items-center justify-center font-bold text-xl border-2 border-line bg-surface-inset" style={{ color: segColors[kunde.segment] || '#338edc' }}>
              {kunde.name.charAt(0)}
            </div>
            <div>
              <h1 className="text-xl font-bold text-content">{kunde.name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                <Badge variant={kunde.segment === 'A' ? 'segment-a' : kunde.segment === 'B' ? 'segment-b' : 'segment-c'}>{t('kunden_detail.segment_label', { seg: kunde.segment })}</Badge>
                {kunde.standort_name && (
                  <Badge variant="default">{kunde.standort_name}</Badge>
                )}
                {sopPct > 0 && (
                  <Badge variant={sopPct >= 50 ? 'success' : 'warning'}>SOP {sopPct.toFixed(0)}%</Badge>
                )}
                <Badge variant="success">{t('common.active')}</Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NavLink to={`/performance?kunde=${encodeURIComponent(kunde.name)}`}>
              <Button variant="secondary" size="sm" className="bg-green-500/10 text-green-400 hover:bg-green-500/20 border-green-500/20">
                {t('kunden_detail.performance_title')} &rarr;
              </Button>
            </NavLink>
            <NavLink to={`/forecast?kunde=${encodeURIComponent(kunde.name)}`}>
              <Button variant="secondary" size="sm" className="bg-primary-500/10 text-primary-400 hover:bg-[#338edc]/20 border-primary-500/20">
                Forecast &rarr;
              </Button>
            </NavLink>
            <Button variant="secondary" size="sm" onClick={copyClaimsLink}>
              {claimsLinkCopied ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
              )}
              {claimsLinkCopied ? t('claims.linkCopied') : t('claims.copyLink')}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              {t('common.edit')}
            </Button>
          </div>
        </div>
      </div>

      {/* ZEITRAUM-TOGGLE + QUICK STATS */}
      {stats && (
        <div className="mb-6">
          <div className="flex items-center gap-2.5 mb-5">
            <span className="text-[11px] font-semibold text-content-tertiary uppercase tracking-wider">{t('kunden_detail.period_heading')}</span>
            <div className="flex bg-surface-inset rounded-[10px] p-[3px] border border-line gap-0.5">
              {[30, 90, 180, 365].map(tp => (
                <button key={tp} onClick={() => setTage(tp)}
                  className={`px-3.5 py-[5px] rounded-lg text-[13px] transition-colors ${tage === tp ? 'bg-surface-card text-content font-semibold shadow-sm' : 'text-content-secondary hover:text-content'}`}>
                  {t(`kunden_detail.period.${tp}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-surface-card border border-line rounded-card px-[22px] py-[18px]">
              <div className="text-[11px] font-semibold text-content-secondary uppercase tracking-wide mb-1.5">{t('kunden_detail.orders')}</div>
              <div className="text-[24px] font-bold tracking-tight text-content">{stats.auftraege.toLocaleString(locale)}</div>
              <div className="text-[11px] text-content-tertiary mt-0.5">{t(`kunden_detail.period.${tage}`)}</div>
            </div>
            <div className="bg-surface-card border border-line rounded-card px-[22px] py-[18px]">
              <div className="text-[11px] font-semibold text-content-secondary uppercase tracking-wide mb-1.5">{t('kunden_detail.outbound_revenue_stat')}</div>
              <div className="text-[24px] font-bold tracking-tight text-content">{fmtEur(stats.umsatz, locale)}</div>
              <div className="text-[11px] text-content-tertiary mt-0.5">{t('kunden_detail.shipping_only')}</div>
            </div>
            <div className="bg-surface-card border border-line rounded-card px-[22px] py-[18px]">
              <div className="text-[11px] font-semibold text-content-secondary uppercase tracking-wide mb-1.5">{t('kunden_detail.cm_short')}</div>
              <div className="text-[24px] font-bold tracking-tight text-content">{fmtEur(stats.db, locale)}</div>
              <div className="flex items-center gap-1.5 mt-1">
                {stats.db_pct != null && <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${stats.db_pct >= 20 ? 'bg-emerald-500/15 text-emerald-400' : stats.db_pct >= 10 ? 'bg-amber-500/15 text-amber-400' : 'bg-red-500/15 text-red-400'}`}>{stats.db_pct}%</span>}
                <span className="text-[11px] text-content-tertiary">{t('kunden_detail.margin_label')}</span>
              </div>
            </div>
            <div className="bg-surface-card border border-line rounded-card px-[22px] py-[18px]">
              <div className="text-[11px] font-semibold text-content-secondary uppercase tracking-wide mb-1.5">{t('kunden_detail.avg_weight_short')}</div>
              <div className="text-[24px] font-bold tracking-tight text-content">{weightKpi ? weightKpi.avg_kg.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' kg' : '–'}</div>
              {weightKpi?.delta_pct != null && (
                <div className="flex items-center gap-1.5 mt-1">
                  <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${weightKpi.delta_pct > 1 ? 'bg-emerald-500/15 text-emerald-400' : weightKpi.delta_pct < -1 ? 'bg-red-500/15 text-red-400' : 'bg-surface-active text-content-secondary'}`}>{weightKpi.delta_pct > 0 ? '+' : ''}{weightKpi.delta_pct.toFixed(1)}%</span>
                  <span className="text-[11px] text-content-tertiary">{t('kunden_detail.vs_prev_month')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* === CARDS === */}

      {/* Row 1: Zeitprofil + Lagervolumen */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <CardZeitprofil kunde={kunde} onEdit={() => setEditOpen(true)} />
        <CardLagervolumen kundeId={kundeId} />
      </div>

      {/* Row 2: Leistungen (BillingTracker) + Paketgewicht */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <CardBillingTracker kundeId={kundeId} />
        <CardPaketgewicht kundeId={kundeId} tage={tage} />
      </div>

      {/* Row 3: Umsatz Gesamt (full width) */}
      <div className="mb-5">
        <CardUmsatzGesamt kundeId={kundeId} />
      </div>

      {/* Row 4: Deckungsbeitrag (full width) */}
      <div className="mb-5">
        <CardDeckungsbeitrag kundeId={kundeId} tage={tage} />
      </div>

      {/* Row 5: Performance (full width with pick/pack side by side) */}
      <div className="mb-5">
        <CardPerformance kundeId={kundeId} tage={tage} />
      </div>

      {/* Row 6: Monats-Statistik (Billing — full width) */}
      <div className="mb-5">
        <CardMonatsStatistik kundeId={kundeId} />
      </div>

      {/* Row 7: Forecast vs IST + Versendete Pakete */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <CardForecastVsIst kundeId={kundeId} />
        <CardVersendetePakete kundeId={kundeId} />
      </div>

      {/* Row 7b: Aktionszeiträume */}
      <div className="mb-5">
        <CardAktionszeitraeume kundeId={kundeId} />
        <CardCommitments kundeId={kundeId} />
      </div>

      {/* Row 8: Rechnungen + Cases + Portal-Zugang + Segment */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <div className="space-y-4">
          <CardSegment kunde={kunde} onUpdated={load} />
          <CardSlaKlasse kunde={kunde} onUpdated={load} />
          <CardHotlineKennwort kundeId={kundeId} />
          <CardRechnungen kundeId={kundeId} />
        </div>
        <div className="space-y-4">
          <CardEmailAdressen kundeId={kundeId} />
          <CardPortalZugang kundeId={kundeId} />
          <CardCases kundeId={kundeId} odooPartnerId={kunde.odoo_partner_id} />
          <CardZufriedenheit kundeId={kundeId} />
        </div>
      </div>

      {/* Row 7: Changelog / Verlauf */}
      <CardChangelog kundeId={kundeId} />

      {/* Row 8: Chatter (interne Kommunikation) — nur wenn aktiviert */}
      {chatterEnabled && (
        <div className="mt-5">
          <UniversalChatter entityType="kunde" entityId={kundeId} />
        </div>
      )}

      {/* EDIT MODAL */}
      <TabbedEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        kunde={kunde}
        standorte={standorte}
        onSaved={load}
      />
    </div>
  );
}
